import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
	LogOut,
	Settings,
	ChevronRight,
	ExternalLink,
	X,
	Users,
	ShieldCheck,
	Trash2,
	Database,
	LayoutDashboard,
	Zap,
	Square,
	Star,
	Webhook,
	Route,
	ListOrdered,
	Activity,
	Loader2,
	Archive,
	RotateCcw,
	Clock,
	MapPin,
	UserCog,
	Search,
	Unlock,
	Upload,
	Check,
	ChevronsUpDown,
	AlertTriangle,
	Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "@/components/ui/dialog";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetDescription,
} from "@/components/ui/sheet";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarProvider,
	SidebarInset,
	SidebarTrigger,
	useSidebar,
} from "@/components/ui/sidebar";
import IntellendLogo from "@/components/IntellendLogo";

import AdminAnalytics from "@/components/AdminAnalytics";
import JourneyAnalysis from "@/components/JourneyAnalysis";
import FunnelDashboard from "@/components/FunnelDashboard/FunnelDashboard";
import CaprionJourneyStatus from "./CaprionJourneyStatus";
import LinkClickAnalytics from "@/components/LinkClickAnalytics";
import JourneyStageAnalytics from "@/components/JourneyStageAnalytics";
import JourneyUrlTimeAnalysis from "@/components/JourneyUrlTimeAnalysis";
import CRMLeadForm from "@/CRM/pages/CRMLeadForm";
import CRMConfig from "@/CRM/pages/CRMConfig";
import CRMBulkUpload from "../CRM/pages/CRMBulkUpload";
import CRMLeadTable from "@/CRM/pages/CRMLeadTable";
import UserManagement from "./UserManagement";
import { useToast } from "@/hooks/use-toast";
import { crmService } from "@/CRM/crmApi";
import RoleManagement from "./RoleManagement";
import ManagePreApproved from "./ManagePreApproved";
import ManageWhitelistPincode from "./ManageWhitelistPincode";
import SetJourney from "./SetJourney";
import {
	adminService,
	type CaprionPendingMandateRow,
	type AdminUserProfile,
	type AdminUserProfileUpdate,
	type AdminClearJourneyMetaResponse,
	type CaprionViewFailuresResponse,
	type CaprionApiFailureRow,
	type VerificationFailureRow,
} from "./adminApi";
import FixerPanel from "./FixerPanel";
import PendingDealerApprovals from "./PendingDealerApprovals";
// Expenses — Digitap vendor-API spend/observability panel.
// @origin: v2 | @since: 0570ba2d | @used-by: ALL
// Ported from v2's `v2/uat/digitap_monitor` (commit 0570ba2d), which mounted it
// exactly this way: a static import + an `AdminView` id, not a lazy route. Kept
// static to match every other panel in this file (FixerPanel, the lms_staff
// screens) -- nothing here is lazy-loaded, so a dynamic import would be a new
// pattern rather than an existing one.
import ExpensesPanel from "./ExpensesPanel";
// PHASE B5g (v2->v3 port, LMS-Staff-Ops epic) -- ported from v2 commits
// 2756e244 ("Shadow LMS") and 31e683cd ("Bodhi view"). See lms_staff/*'s own
// module docstrings for the RBAC/routing rationale.
import LmsStaffOpsHub from "./lms_staff/LmsStaffOpsHub";
import LmsPermissionsAdmin from "./lms_staff/LmsPermissionsAdmin";
import DisbursedLoansTable from "./lms_staff/DisbursedLoansTable";
import UploadDailyCollection from "./lms_staff/UploadDailyCollection";
import IntraDayBounceView from "./lms_staff/IntraDayBounceView";
import ThuriyamDashboard from "./thuriyam/ThuriyamDashboard";
import { SIDEBAR_CONFIG, FOOTER_CONFIG, MenuCategory, MenuItem } from "@/config/menuConfig";
import { PETPOOJA_SAMPLE_DATA, shiftToYesterday } from "@/data/petpoojaSampleData";

/**
 * The set of dashboard views. Extracted into a named type -- see
 * v2 commit 2756e244's rationale: this union was previously written out
 * twice (useState + handleViewChange), which meant every new view had to be
 * added in both places or the second copy would reject it.
 * `lms-staff-ops` / `lms-staff-permissions` / `lms-disbursed-loans` are the
 * Parallel LMS Staff Management System entry points (PHASE B5g port).
 */
type AdminView =
	| "tools"
	| "analytics"
	| "journey-analysis"
	| "funnel-dashboard"
	| "caprion-journey-status"
	| "stage-analytics"
	| "journey-url-analysis"
	| "link-click"
	| "crm-leads"
	| "crm-settings"
	| "crm-config"
	| "bulk-upload"
	| "leads-table"
	| "todays-tasks"
	| "tomorrows-tasks"
	| "general-settings"
	| "users"
	| "roles"
	| "iadmin-system"
	| "iadmin-access"
	| "iadmin-danger"
	| "iadmin-preapproved"
	| "iadmin-whitelist-pincode"
	| "iadmin-set-journey"
	| "iadmin-fixer"
	| "iadmin-expenses"
	| "lms-staff-ops"
	| "lms-staff-permissions"
	| "lms-disbursed-loans"
	| "lms-upload-daily-collection"
	| "lms-intraday-bounce"
	| "thuriyam-dashboard";

/**
 * document_type options for the Caprion "Upload Document" tool.
 *
 * Caprion's UploadDocument API accepts document_type as a free-text string, so
 * this list is a UI convenience — it stops operators inventing a dozen
 * spellings of the same document ("aadhar" / "Aadhaar Card" / "AADHAAR") which
 * would make the uploads impossible to audit by type later. The combobox is
 * searchable because the list is long enough that scanning it is slower than
 * typing.
 *
 * Module-level (not inside the component) so it is allocated once rather than
 * rebuilt on every render.
 */
const CAPRION_DOCUMENT_TYPES = [
	"NACH",
	"Signed Agreement",
	"PDC",
	"Cancelled Cheque",
	"Invoice",
	"Advanced Payment / Downpayment Proof",
	"PAN Card",
	"Aadhaar Card",
	"Bank Statement",
	"Income Proof",
	"Address Proof",
	"Photo",
	"Other",
	"Agreement Letter (Signed)",
	"Borrower Signature",
	"Co-applicant Signature",
	"Cheque",
	"KFS",
	"Sanction Letter (Signed)",
	"Identity Proof",
	"Passport",
	"Driving License",
	"Utility Bill",
	"Rental Agreement",
] as const;

const AdminDashboardContent = () => {
	const { leadId } = useParams<{ leadId?: string }>();
	const { user, logout } = useAuth();
	const { isMobile, setOpenMobile, toggleSidebar, state, openMobile } = useSidebar();
	
	const isInternalUser = user?.user_type?.toUpperCase() === "INTERNAL";
	const isBapUser = user?.user_type?.toUpperCase() === "BAP";
	const isLeUser = user?.user_type?.toUpperCase() === "LE";

	const isAdmin = !!user?.is_superuser;
	const roles = user?.roles || [];
	const permissions = user?.permissions || [];

	const canAccess = (item: MenuCategory | MenuItem): boolean => {
		if (isAdmin) return true;
		if (item.isSuperuserOnly && !isAdmin) return false;
		
		// 1. Check Dynamic Permissions for single items
		if ('id' in item && permissions.includes(item.id)) {
			return true;
		}
        
		// 2. For categories, check if any of their items are allowed
		if ('items' in item) {
			const hasVisibleChild = item.items.some(child => canAccess(child));
			if (hasVisibleChild) return true;
		}

		// 3. Fallback to Hardcoded UserType (if defined)
		if (item.userTypes && item.userTypes.length > 0) {
			return !!(user?.user_type && item.userTypes.includes(user.user_type.toUpperCase()));
		}
		
		return false; 
	};

	// Keep these for internal logic/redirects if needed, but we'll use canAccess for sidebar
	const canAccessCRM = canAccess({ id: 'crm' } as any);
	const canAccessTools = canAccess({ id: 'tools' } as any);
	const canAccessAnalytics = canAccess({ id: 'analytics' } as any);
	const canAccessCRMConfig = canAccessCRM; 
	const canAccessCSuiteMIS = isAdmin || (canAccessAnalytics && isInternalUser); 
	const canAccessJourneyAnalysis = canAccessAnalytics;

	const [activeView, setActiveView] = React.useState<AdminView>(() => {
		// Restore the last-visited tab on refresh, falling back to the user's
		// default_landing_page or the role-based default.
		const saved = localStorage.getItem('admin_active_view') as AdminView | null;
		const fallback: AdminView = (user?.default_landing_page as any) || (isAdmin ? "tools" : isInternalUser ? "crm-leads" : isBapUser ? "journey-analysis" : "tools");
		return saved || fallback;
	});
	// @origin: v2 | @since: 3d9ebf6d | @used-by: ALL | @purpose: 2f port --
	// tracks which user's URL/Time analysis is being viewed when navigated to
	// from JourneyAnalysis.tsx's per-row action button.
	const [urlAnalysisUserId, setUrlAnalysisUserId] = React.useState<number | null>(null);
	const [leadFormDisplayMode, setLeadFormDisplayMode] = React.useState<"single" | "story">("single");
	const [leadTableDisplayMode, setLeadTableDisplayMode] = React.useState<"excel" | "modern">("excel");
	const [timezone, setTimezone] = React.useState<string>("+05:30");
	const [layout, setLayout] = React.useState<"compact" | "standard" | "spacious">("spacious");

	const { toast } = useToast();

	const [deleteJourneyOpen, setDeleteJourneyOpen] = React.useState(false);
	const [deleteJourneyUserId, setDeleteJourneyUserId] = React.useState("");
	const [deleteJourneyPhone, setDeleteJourneyPhone] = React.useState("");
	const [deleteJourneyLoading, setDeleteJourneyLoading] = React.useState(false);

	// ── Archive User ──────────────────────────────────────────────────────────
	// "Preview" (dry_run=true) is OPTIONAL — it shows the exact rename, the
	// sessions that will die and the phone-keyed rows the next signup inherits,
	// but Archive works without it. Both user ID and mobile number are required
	// either way, and the backend refuses unless they resolve to the SAME row,
	// so skipping the preview cannot hit the wrong borrower.
	const [archiveUserOpen, setArchiveUserOpen] = React.useState(false);
	const [archiveUserId, setArchiveUserId] = React.useState("");
	const [archiveUsername, setArchiveUsername] = React.useState("");
	// Which request is in flight — needed to put the spinner on the right button,
	// since either can be pressed first.
	const [archiveBusy, setArchiveBusy] = React.useState<"preview" | "archive" | null>(null);
	const [archivePreview, setArchivePreview] = React.useState<any>(null);
	const [archiveError, setArchiveError] = React.useState<string | null>(null);

	// ── Reset Journey ─────────────────────────────────────────────────────────
	// Load the user's pages (1..N, page 1 == checkpoint_index 0) then set their
	// position to the one picked from the dropdown. Same effect as
	// `manage.py reset_journey --user <id> --page-number <n>`.
	// Keyed on the mobile number (which IS the auth_user username) because that
	// is what support/QA actually have; a numeric input is treated as a user ID
	// only when it clearly is not a phone number (see buildResetIdentifier).
	// Pending Dealer Approval dialog (Caprion Tools) — lists the late-night
	// deferral queue and lets an operator release one now. See
	// PendingDealerApprovals.tsx.
	const [pendingDealerApprovalOpen, setPendingDealerApprovalOpen] = React.useState(false);

	const [resetJourneyOpen, setResetJourneyOpen] = React.useState(false);
	const [resetJourneyIdentifier, setResetJourneyIdentifier] = React.useState("");
	const [resetJourneyPages, setResetJourneyPages] = React.useState<any>(null);
	const [resetJourneyTarget, setResetJourneyTarget] = React.useState("");
	const [resetJourneyBusy, setResetJourneyBusy] = React.useState<"load" | "apply" | null>(null);
	const [resetJourneyError, setResetJourneyError] = React.useState<string | null>(null);

	// Petpooja Dump dialog state
	const [petpoojaDumpOpen, setPetpoojaDumpOpen] = React.useState(false);
	const [petpoojaDumpJson, setPetpoojaDumpJson] = React.useState("");
	const [petpoojaDumpLoading, setPetpoojaDumpLoading] = React.useState(false);

	// Petpooja Dump list/viewer state
	const [dumpsViewerOpen, setDumpsViewerOpen] = React.useState(false);
	const [dumpsList, setDumpsList] = React.useState<any[]>([]);
	const [dumpsTotal, setDumpsTotal] = React.useState(0);
	const [dumpsPage, setDumpsPage] = React.useState(1);
	const [dumpsSearch, setDumpsSearch] = React.useState("");
	const [dumpsLoading, setDumpsLoading] = React.useState(false);

	// ── Caprion: Fetch RPS ────────────────────────────────────────────────────
	const [caprionRpsAppId, setCaprionRpsAppId] = React.useState("");
	const [caprionRpsLoading, setCaprionRpsLoading] = React.useState(false);
	const [caprionRpsResult, setCaprionRpsResult] = React.useState<any>(null);
	const [caprionRpsOpen, setCaprionRpsOpen] = React.useState(false);
	// "formatted" = pretty table view; "raw" = raw JSON <pre> block
	const [caprionRpsViewMode, setCaprionRpsViewMode] = React.useState<"formatted" | "raw">("formatted");

	// ── Caprion: Create Non-Captive Lead ──────────────────────────────────────
	const [caprionCreateLeadOpen, setCaprionCreateLeadOpen] = React.useState(false);
	const [caprionCreateLeadJson, setCaprionCreateLeadJson] = React.useState("");
	const [caprionCreateLeadLoading, setCaprionCreateLeadLoading] = React.useState(false);
	const [caprionCreateLeadResult, setCaprionCreateLeadResult] = React.useState<any>(null);

	const handleCaprionCreateLead = async () => {
		const trimmed = caprionCreateLeadJson.trim();
		if (!trimmed) return;
		let parsed: Record<string, any>;
		try {
			parsed = JSON.parse(trimmed);
		} catch {
			toast({ title: "Invalid JSON", description: "Please paste valid JSON before submitting.", variant: "destructive" });
			return;
		}
		setCaprionCreateLeadLoading(true);
		setCaprionCreateLeadResult(null);
		try {
			const res = await adminService.caprionCreateLead(parsed);
			setCaprionCreateLeadResult(res.data);
		} catch (err: any) {
			setCaprionCreateLeadResult({ error: err?.response?.data?.error ?? String(err) });
		} finally {
			setCaprionCreateLeadLoading(false);
		}
	};

	// ── Caprion: Generate Initial Offer ───────────────────────────────────────
	// Creates the initial BRELoanOffers row for an application when one is
	// missing (the usual cause: the borrower's page-7→8 transition failed, so
	// the offer was never generated and the KFS/Final Offer pages have nothing
	// to read). Only the application_id is needed — the backend resolves the
	// borrower from the application's BRE2 approval and reuses the same offer
	// maths the journey itself runs.
	//
	// Idempotent: if an offer already exists the call reports it and generates
	// nothing, so a double click cannot create a second offer that would then
	// win the journey's "latest offer" lookups.
	const [caprionGenOfferOpen, setCaprionGenOfferOpen] = React.useState(false);
	const [caprionGenOfferAppId, setCaprionGenOfferAppId] = React.useState("");
	const [caprionGenOfferLoading, setCaprionGenOfferLoading] = React.useState(false);
	const [caprionGenOfferResult, setCaprionGenOfferResult] = React.useState<any>(null);

	const resetCaprionGenOfferDialog = () => {
		setCaprionGenOfferOpen(false);
		setCaprionGenOfferAppId("");
		setCaprionGenOfferResult(null);
	};

	const handleCaprionGenerateInitialOffer = async () => {
		const appId = caprionGenOfferAppId.trim();
		if (!appId) return;
		setCaprionGenOfferLoading(true);
		setCaprionGenOfferResult(null);
		try {
			const res = await adminService.caprionGenerateInitialOffer(appId);
			setCaprionGenOfferResult(res.data);
			toast({
				title: res.data?.created ? "Offer generated" : "Offer already exists",
				description: res.data?.detail ?? "",
			});
		} catch (err: any) {
			setCaprionGenOfferResult({ error: err?.response?.data?.error ?? String(err) });
		} finally {
			setCaprionGenOfferLoading(false);
		}
	};

	// ── Caprion: Upload Document ──────────────────────────────────────────────
	// Pushes one or more files to Caprion's UploadDocument API for an existing
	// application. The operator types ONLY the application_id — the backend
	// resolves applicant_id from the CaprionRequestResponse audit trail, so a
	// document cannot be filed against the wrong borrower by a mistyped id.
	// The call is logged to CaprionRequestResponse against that application_id.
	const [caprionUploadDocOpen, setCaprionUploadDocOpen] = React.useState(false);
	const [caprionUploadAppId, setCaprionUploadAppId] = React.useState("");
	const [caprionUploadDocType, setCaprionUploadDocType] = React.useState("");
	// Controls the document_type combobox popover (searchable dropdown).
	const [caprionDocTypeOpen, setCaprionDocTypeOpen] = React.useState(false);
	const [caprionUploadRemarks, setCaprionUploadRemarks] = React.useState("");
	const [caprionUploadFiles, setCaprionUploadFiles] = React.useState<File[]>([]);
	const [caprionUploadLoading, setCaprionUploadLoading] = React.useState(false);
	const [caprionUploadResult, setCaprionUploadResult] = React.useState<any>(null);
	// Lives on the ref so the same <input type="file"> can be cleared after a
	// send; without the reset, re-picking the SAME filename fires no change
	// event and the operator thinks the dialog is ignoring them.
	const caprionUploadInputRef = React.useRef<HTMLInputElement | null>(null);

	const resetCaprionUploadDocDialog = () => {
		setCaprionUploadDocOpen(false);
		setCaprionDocTypeOpen(false);
		setCaprionUploadAppId("");
		setCaprionUploadDocType("");
		setCaprionUploadRemarks("");
		setCaprionUploadFiles([]);
		setCaprionUploadResult(null);
		if (caprionUploadInputRef.current) caprionUploadInputRef.current.value = "";
	};

	const handleCaprionUploadDocument = async () => {
		const appId = caprionUploadAppId.trim();
		const docType = caprionUploadDocType.trim();
		if (!appId || !docType || caprionUploadFiles.length === 0) return;

		setCaprionUploadLoading(true);
		setCaprionUploadResult(null);
		try {
			const res = await adminService.caprionUploadDocument({
				application_id: appId,
				document_type: docType,
				files: caprionUploadFiles,
				remarks: caprionUploadRemarks.trim() || undefined,
			});
			setCaprionUploadResult(res.data);
			if (res.data?.success) {
				toast({
					title: "Document uploaded",
					description: `${res.data.files_sent?.length ?? caprionUploadFiles.length} file(s) sent to Caprion for application ${appId}.`,
				});
				// Clear only the file picker — the operator usually uploads several
				// document types for the SAME application, so the id is kept.
				setCaprionUploadFiles([]);
				if (caprionUploadInputRef.current) caprionUploadInputRef.current.value = "";
			}
		} catch (err: any) {
			setCaprionUploadResult({ error: err?.response?.data?.error ?? String(err) });
		} finally {
			setCaprionUploadLoading(false);
		}
	};

	// ── Caprion: Manage Mandates (list + per-row verify) ──────────────────────
	// Lists every not-yet-verified mandate for the PhonePe BAP. Verifying a row
	// writes status=accepted server-side, which takes it out of the list's filter —
	// so we simply refetch and the row is gone.
	const [caprionManageMandatesOpen, setCaprionManageMandatesOpen] = React.useState(false);
	const [caprionMandateRows, setCaprionMandateRows] = React.useState<CaprionPendingMandateRow[]>([]);
	const [caprionMandateRowsLoading, setCaprionMandateRowsLoading] = React.useState(false);
	const [caprionMandateRowsError, setCaprionMandateRowsError] = React.useState<string | null>(null);
	// mandate_id of the row currently being verified — used to show a per-row
	// spinner and to disable only that row's button, not the whole table.
	const [caprionVerifyingRowId, setCaprionVerifyingRowId] = React.useState<string | null>(null);
	// Per-row outcome message, keyed by mandate_id. Kept for rows that did NOT
	// disappear (i.e. Digio has still not confirmed them) so the admin can see why
	// nothing changed instead of the click looking like a no-op.
	const [caprionRowNotes, setCaprionRowNotes] = React.useState<Record<string, string>>({});

	// ── Caprion: View Failures ────────────────────────────────────────────────
	// Two read-only failure feeds for ONE borrower, in one dialog:
	//   "Page 11" — failed Caprion API calls, save_aadhaar_details first
	//   "Page 15" — failed verifications with their message / remark
	//
	// PER-BORROWER: nothing is fetched until the operator enters a mobile
	// number, so opening the dialog shows a prompt rather than every failure in
	// the system. Read-only: the backend never calls Caprion's live API, so
	// re-running a lookup is free.
	const [caprionFailuresOpen, setCaprionFailuresOpen] = React.useState(false);
	const [caprionFailures, setCaprionFailures] = React.useState<CaprionViewFailuresResponse | null>(null);
	const [caprionFailuresLoading, setCaprionFailuresLoading] = React.useState(false);
	const [caprionFailuresError, setCaprionFailuresError] = React.useState<string | null>(null);
	// The mobile number typed into the dialog. Mobile number IS auth_user.username
	// here, same identifier convention as the Edit User / Archive User tools.
	const [caprionFailuresMobile, setCaprionFailuresMobile] = React.useState("");
	// Response JSON is shown INLINE for every row (it carries the lender's
	// actual reason, which is what the operator came for). The REQUEST is the
	// bulky one — okyc_xml alone can run to 150k chars — so it sits behind a
	// per-row toggle. This set holds the row ids whose request is expanded.
	const [caprionFailuresOpenRequests, setCaprionFailuresOpenRequests] = React.useState<Set<number>>(new Set());

	const toggleCaprionFailureRequest = (id: number) => {
		setCaprionFailuresOpenRequests(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id); else next.add(id);
			return next;
		});
	};

	const resetCaprionFailuresDialog = () => {
		setCaprionFailures(null);
		setCaprionFailuresError(null);
		setCaprionFailuresMobile("");
		setCaprionFailuresOpenRequests(new Set());
	};

	const fetchCaprionFailures = async (mobile: string) => {
		const trimmed = (mobile ?? "").trim();
		// Guard here as well as disabling the button: Enter-to-search would
		// otherwise fire an unscoped request on an empty box.
		if (!trimmed) return;
		setCaprionFailuresLoading(true);
		setCaprionFailuresError(null);
		// Drop the previous borrower's rows immediately so a slow request can
		// never leave one user's failures on screen under another's number.
		setCaprionFailures(null);
		setCaprionFailuresOpenRequests(new Set());
		try {
			const res = await adminService.caprionViewFailures({ mobile: trimmed });
			setCaprionFailures(res.data);
		} catch (err: any) {
			setCaprionFailuresError(err?.response?.data?.error ?? String(err));
			setCaprionFailures(null);
		} finally {
			setCaprionFailuresLoading(false);
		}
	};

	const fetchCaprionPendingMandates = async () => {
		setCaprionMandateRowsLoading(true);
		setCaprionMandateRowsError(null);
		try {
			const res = await adminService.caprionPendingMandates();
			setCaprionMandateRows(res.data.results ?? []);
		} catch (err: any) {
			setCaprionMandateRowsError(err?.response?.data?.error ?? String(err));
			setCaprionMandateRows([]);
		} finally {
			setCaprionMandateRowsLoading(false);
		}
	};

	const handleCaprionVerifyRow = async (row: CaprionPendingMandateRow) => {
		setCaprionVerifyingRowId(row.mandate_id);
		// Drop any previous note for this row so a stale "not confirmed" message
		// cannot linger next to a fresh attempt.
		setCaprionRowNotes(prev => {
			const next = { ...prev };
			delete next[row.mandate_id];
			return next;
		});
		try {
			const res = await adminService.caprionVerifyMandate({
				mandate_id: row.mandate_id,
				// Lender is fixed to CAPRION here: this list is the Caprion tool and
				// every row in it is a Caprion mandate, so there is nothing to choose.
				lender_name: "CAPRION",
			});
			const data: any = res.data;
			if (data?.is_success) {
				// Verified → the backend wrote status=accepted, so the row no longer
				// matches this list's filter. Refetch and it disappears.
				await fetchCaprionPendingMandates();
			} else {
				// Still unconfirmed by Digio — the row stays. Surface the reason
				// in-row rather than silently doing nothing.
				setCaprionRowNotes(prev => ({
					...prev,
					[row.mandate_id]: data?.message ?? "Digio has not confirmed this mandate yet.",
				}));
			}
		} catch (err: any) {
			setCaprionRowNotes(prev => ({
				...prev,
				[row.mandate_id]: err?.response?.data?.error ?? String(err),
			}));
		} finally {
			setCaprionVerifyingRowId(null);
		}
	};

	// ── Caprion: Trigger Verify Mandates ──────────────────────────────────────
	// Re-runs the Digio mandate status check for ONE stuck mandate and persists
	// status=4 so the borrower's eMandate page shows it as verified. Keyed by
	// mandate_id (the Digio mandate id, same value Caprion calls
	// merchant_request_number) because the UMRN does not exist until NPCI confirms.
	const [caprionVerifyMandateOpen, setCaprionVerifyMandateOpen] = React.useState(false);
	const [caprionMandateId, setCaprionMandateId] = React.useState("");
	// "" = None → backend resolves the lender from the user's journey. Only the
	// lenders that actually run Digio mandates belong in this list; the value must
	// match Lender.name in the DB (looked up name__iexact), see
	// backend/registry/tenant_codes.py LND_CAPRION.
	const [caprionMandateLender, setCaprionMandateLender] = React.useState("");
	const [caprionMandateLoading, setCaprionMandateLoading] = React.useState(false);
	const [caprionMandateResult, setCaprionMandateResult] = React.useState<any>(null);

	const resetCaprionVerifyMandateDialog = () => {
		setCaprionMandateId("");
		setCaprionMandateLender("");
		setCaprionMandateResult(null);
	};

	// mandate_id alone is enough: it is unique, so the backend derives the mandate's
	// owner and its UPI-vs-NACH type from the row itself.
	const handleCaprionVerifyMandate = async () => {
		const mandateId = caprionMandateId.trim();
		if (!mandateId) return;

		setCaprionMandateLoading(true);
		setCaprionMandateResult(null);
		try {
			const res = await adminService.caprionVerifyMandate({
				mandate_id: mandateId,
				...(caprionMandateLender ? { lender_name: caprionMandateLender } : {}),
			});
			setCaprionMandateResult(res.data);
		} catch (err: any) {
			setCaprionMandateResult({ error: err?.response?.data?.error ?? String(err) });
		} finally {
			setCaprionMandateLoading(false);
		}
	};

	// ── Caprion: Cancel Lead ──────────────────────────────────────────────────
	const [caprionCancelLeadOpen, setCaprionCancelLeadOpen] = React.useState(false);
	const [caprionCancelAppId, setCaprionCancelAppId] = React.useState("");
	const [caprionCancelApplicantId, setCaprionCancelApplicantId] = React.useState("");
	const [caprionCancelRemarks, setCaprionCancelRemarks] = React.useState("");
	const [caprionCancelLoading, setCaprionCancelLoading] = React.useState(false);
	const [caprionCancelResult, setCaprionCancelResult] = React.useState<any>(null);

	const handleCaprionCancelLead = async () => {
		if (!caprionCancelAppId.trim() || !caprionCancelApplicantId.trim() || !caprionCancelRemarks.trim()) return;
		setCaprionCancelLoading(true);
		setCaprionCancelResult(null);
		try {
			const res = await adminService.caprionCancelLead({
				application_id: caprionCancelAppId.trim(),
				applicant_id: caprionCancelApplicantId.trim(),
				remarks: caprionCancelRemarks.trim(),
			});
			setCaprionCancelResult(res.data);
		} catch (err: any) {
			setCaprionCancelResult({ error: err?.response?.data?.error ?? String(err) });
		} finally {
			setCaprionCancelLoading(false);
		}
	};

	const handleCaprionFetchRps = async () => {
		if (!caprionRpsAppId.trim()) return;
		setCaprionRpsLoading(true);
		setCaprionRpsResult(null);
		try {
			const res = await adminService.caprionFetchRps(caprionRpsAppId.trim());
			setCaprionRpsResult(res.data);
		} catch (err: any) {
			setCaprionRpsResult({ error: err?.response?.data?.error ?? String(err) });
		} finally {
			setCaprionRpsLoading(false);
		}
	};

	// ── Caprion: Check Status ─────────────────────────────────────────────────
	const [caprionStatusMode, setCaprionStatusMode] = React.useState<"application_id" | "urn">("application_id");
	const [caprionStatusValue, setCaprionStatusValue] = React.useState("");
	const [caprionStatusLoading, setCaprionStatusLoading] = React.useState(false);
	const [caprionStatusResult, setCaprionStatusResult] = React.useState<any>(null);
	const [caprionStatusOpen, setCaprionStatusOpen] = React.useState(false);

	// Address Match Report — read-only, safe to re-run (no lender call).
	const [addrReportOpen, setAddrReportOpen] = React.useState(false);
	const [addrReportMobile, setAddrReportMobile] = React.useState("");
	const [addrReportLoading, setAddrReportLoading] = React.useState(false);
	const [addrReportText, setAddrReportText] = React.useState<string>("");
	const [addrReportError, setAddrReportError] = React.useState<string>("");
	const [addrReportCopied, setAddrReportCopied] = React.useState(false);

	// ── Address CPV range report (the second tab) ────────────────────────────
	//
	// start/end are 1-based INCLUSIVE POSITIONS in the borrower list, not user
	// ids, so "1 to 50" means the first fifty borrowers. The work runs as a
	// Celery task because it is far too slow per borrower to survive a single
	// request, so this polls for progress and only then enables the download.
	const [addrRangeStart, setAddrRangeStart] = React.useState("1");
	const [addrRangeEnd, setAddrRangeEnd] = React.useState("50");
	// Explicit mobile list, which takes precedence over the range when filled.
	const [addrRangeMobiles, setAddrRangeMobiles] = React.useState("");
	const [addrRangeTaskId, setAddrRangeTaskId] = React.useState("");
	const [addrRangeRunning, setAddrRangeRunning] = React.useState(false);
	const [addrRangeDone, setAddrRangeDone] = React.useState(0);
	const [addrRangeTotal, setAddrRangeTotal] = React.useState(0);
	// Only counts are kept, not the rows themselves: the output is the Excel
	// file, which the backend builds from the task's stored result, so holding
	// a copy of every row in component state would serve no purpose.
	const [addrRangeReady, setAddrRangeReady] = React.useState(false);
	const [addrRangeRowCount, setAddrRangeRowCount] = React.useState(0);
	const [addrRangeFailed, setAddrRangeFailed] = React.useState(0);
	const [addrRangeError, setAddrRangeError] = React.useState("");

	// True as soon as the box holds at least one digit — gates the mobiles-tab
	// Run button so an empty paste cannot be submitted.
	const hasAddrRangeMobiles = /\d/.test(addrRangeMobiles);

	// Which selection produced the CURRENT result. Recorded at submit time and
	// used only for the download filename, so a file is never named after the
	// tab the operator happens to be looking at afterwards.
	const [addrRangeMode, setAddrRangeMode] = React.useState<"range" | "mobiles">("range");

	// Held in a ref, not state: the interval must be clearable from inside its
	// own callback and from the unmount cleanup, and a state update there would
	// just schedule another render for no reason.
	const addrRangePollRef = React.useRef<number | null>(null);

	const stopAddrRangePoll = React.useCallback(() => {
		if (addrRangePollRef.current !== null) {
			window.clearInterval(addrRangePollRef.current);
			addrRangePollRef.current = null;
		}
	}, []);

	// Never leave a timer running after the component goes away.
	React.useEffect(() => stopAddrRangePoll, [stopAddrRangePoll]);

	// `mode` is passed in by the calling tab rather than inferred from which
	// fields happen to be filled: the tab the operator is looking at IS the
	// decision, and guessing from input state is how the two tabs would end up
	// disagreeing about what ran.
	const handleAddressCpvRangeRun = async (mode: "range" | "mobiles") => {
		const start = parseInt(addrRangeStart, 10);
		const end = parseInt(addrRangeEnd, 10);

		if (mode === "range") {
			if (!Number.isFinite(start) || !Number.isFinite(end) || start < 1 || end < 1) {
				setAddrRangeError("Enter a start and end of 1 or greater.");
				return;
			}
			if (end < start) {
				setAddrRangeError("End must be greater than or equal to start.");
				return;
			}
		} else if (!hasAddrRangeMobiles) {
			setAddrRangeError("Paste at least one mobile number.");
			return;
		}

		setAddrRangeMode(mode);
		stopAddrRangePoll();
		setAddrRangeRunning(true);
		setAddrRangeError("");
		setAddrRangeReady(false);
		setAddrRangeRowCount(0);
		setAddrRangeFailed(0);
		setAddrRangeDone(0);
		setAddrRangeTotal(0);
		setAddrRangeTaskId("");

		try {
			const res = await adminService.addressCpvRangeStart(
				mode === "mobiles" ? { mobiles: addrRangeMobiles } : { start, end },
			);
			const taskId = res.data?.task_id;
			if (!taskId) throw new Error("No task id returned.");
			setAddrRangeTaskId(taskId);

			addrRangePollRef.current = window.setInterval(async () => {
				try {
					const p = await adminService.addressCpvRangeProgress(taskId);
					const d = p.data ?? {};
					setAddrRangeDone(d.done ?? 0);
					setAddrRangeTotal(d.total ?? 0);

					if (d.state === "SUCCESS") {
						stopAddrRangePoll();
						setAddrRangeRowCount(d.rows?.length ?? d.total ?? 0);
						setAddrRangeFailed(d.failed ?? 0);
						setAddrRangeReady(true);
						setAddrRangeRunning(false);
					} else if (d.state === "FAILURE") {
						stopAddrRangePoll();
						setAddrRangeError(d.error ?? "The report failed.");
						setAddrRangeRunning(false);
					}
				} catch (err: any) {
					// A single failed poll is not fatal on its own, but there is
					// no value in hammering an endpoint that is erroring.
					stopAddrRangePoll();
					setAddrRangeError(err?.response?.data?.error ?? String(err));
					setAddrRangeRunning(false);
				}
			}, 1500);
		} catch (err: any) {
			setAddrRangeError(err?.response?.data?.error ?? String(err));
			setAddrRangeRunning(false);
		}
	};

	const handleAddressCpvRangeDownload = async () => {
		if (!addrRangeTaskId) return;
		try {
			const res = await adminService.addressCpvRangeDownload(addrRangeTaskId);
			// Object URL + synthetic click: the endpoint requires the session
			// cookie and sends Content-Disposition, so a plain window.open on a
			// blob response would not carry the auth or the filename.
			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = url;
			// Named after the mode that PRODUCED this result, not the current
			// tab, so switching tabs after a run cannot mislabel the file.
			link.download = addrRangeMode === "mobiles"
				? `address_cpv_${addrRangeRowCount}_mobiles.xlsx`
				: `address_cpv_${addrRangeStart}_${addrRangeEnd}.xlsx`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (err: any) {
			setAddrRangeError(err?.response?.data?.error ?? String(err));
		}
	};

	// Clear any finished/failed result when the operator switches tabs, so a
	// report built from a range is never offered for download under the mobile
	// list tab (and vice versa). A RUN IN PROGRESS is deliberately left alone:
	// the job is already on the worker, and killing the poll would just orphan
	// it with no way to collect the result.
	const resetAddrRangeResult = () => {
		if (addrRangeRunning) return;
		setAddrRangeReady(false);
		setAddrRangeRowCount(0);
		setAddrRangeFailed(0);
		setAddrRangeError("");
		setAddrRangeTaskId("");
		setAddrRangeDone(0);
		setAddrRangeTotal(0);
	};

	// Progress / error / ready-to-download, shared verbatim by both bulk tabs.
	// Defined once as an element rather than duplicated in each TabsContent so
	// the two can never drift apart.
	const addrRangeStatusPanel = (
		<>
			{addrRangeRunning && (
				<div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-700">
					Building report… {addrRangeDone}
					{addrRangeTotal ? ` / ${addrRangeTotal}` : ""} borrower(s) scored.
				</div>
			)}

			{addrRangeError && (
				<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
					{addrRangeError}
				</div>
			)}

			{/* Deliberately NO on-screen table: the output is an Excel file, so
			    rendering the same rows again would just be a second place to
			    keep in sync. */}
			{addrRangeReady && (
				<div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800 flex items-center justify-between gap-3">
					<span>
						Report ready — {addrRangeRowCount} row(s) scored
						{addrRangeFailed ? `, ${addrRangeFailed} with no usable data` : ""}.
					</span>
					<Button size="sm" onClick={handleAddressCpvRangeDownload}>
						<Download className="h-4 w-4 mr-2" />
						Download Excel
					</Button>
				</div>
			)}
		</>
	);

	const handleCaprionCheckStatus = async () => {
		if (!caprionStatusValue.trim()) return;
		setCaprionStatusLoading(true);
		setCaprionStatusResult(null);
		try {
			const params = caprionStatusMode === "application_id"
				? { application_id: caprionStatusValue.trim() }
				: { urn: caprionStatusValue.trim() };
			const res = await adminService.caprionCheckStatus(params);
			setCaprionStatusResult(res.data);
		} catch (err: any) {
			setCaprionStatusResult({ error: err?.response?.data?.error ?? String(err) });
		} finally {
			setCaprionStatusLoading(false);
		}
	};

	const handleAddressMatchReport = async () => {
		const digits = addrReportMobile.replace(/\D/g, "").slice(-10);
		if (digits.length !== 10) {
			setAddrReportError("Enter a 10-digit mobile number.");
			return;
		}
		setAddrReportLoading(true);
		setAddrReportError("");
		setAddrReportText("");
		setAddrReportCopied(false);
		try {
			const res = await adminService.addressMatchReport({ mobile: digits });
			setAddrReportText(res.data?.text ?? "");
		} catch (err: any) {
			setAddrReportError(err?.response?.data?.error ?? String(err));
		} finally {
			setAddrReportLoading(false);
		}
	};

	// Ref to the report textarea, so the copy fallback can select the real
	// element the operator is already looking at.
	const addrReportRef = React.useRef<HTMLTextAreaElement>(null);

	const copyAddressReport = async () => {
		// Two paths on purpose. `navigator.clipboard` only exists in a secure
		// context, so on plain-HTTP origins (localhost, internal UAT hosts) it is
		// undefined and the modern call cannot work at all. The deprecated
		// `execCommand("copy")` still works there, so it is the fallback rather
		// than an error message — telling an operator to copy by hand is not a
		// fix when the browser can still do it for them.
		const text = addrReportText;

		if (navigator.clipboard && window.isSecureContext) {
			try {
				await navigator.clipboard.writeText(text);
				setAddrReportCopied(true);
				setAddrReportError("");
				setTimeout(() => setAddrReportCopied(false), 2000);
				return;
			} catch {
				// Permission denied or similar — fall through to execCommand.
			}
		}

		try {
			// Select the visible textarea when we have it (keeps the UI honest
			// about what was copied); otherwise use a detached one.
			const node = addrReportRef.current;
			if (node) {
				node.focus();
				node.select();
				const ok = document.execCommand("copy");
				node.setSelectionRange(0, 0);
				node.blur();
				if (!ok) throw new Error("execCommand returned false");
			} else {
				const temp = document.createElement("textarea");
				temp.value = text;
				// Keep it off-screen but still focusable — `display:none` and
				// `visibility:hidden` both make the selection fail.
				temp.style.position = "fixed";
				temp.style.top = "-1000px";
				temp.style.opacity = "0";
				document.body.appendChild(temp);
				temp.focus();
				temp.select();
				const ok = document.execCommand("copy");
				document.body.removeChild(temp);
				if (!ok) throw new Error("execCommand returned false");
			}
			setAddrReportCopied(true);
			setAddrReportError("");
			setTimeout(() => setAddrReportCopied(false), 2000);
		} catch {
			setAddrReportError(
				"Could not copy automatically. Click inside the report and press Ctrl+A then Ctrl+C."
			);
		}
	};

	const resetAddrReportDialog = () => {
		setAddrReportOpen(false);
		setAddrReportMobile("");
		setAddrReportText("");
		setAddrReportError("");
		setAddrReportCopied(false);
		// The range tab shares this dialog, so closing it must also stop any
		// poll still running — otherwise the timer outlives the UI it feeds.
		stopAddrRangePoll();
		setAddrRangeRunning(false);
		setAddrRangeReady(false);
		setAddrRangeRowCount(0);
		setAddrRangeFailed(0);
		setAddrRangeMobiles("");
		setAddrRangeError("");
		setAddrRangeTaskId("");
		setAddrRangeDone(0);
		setAddrRangeTotal(0);
	};

	const resetDeleteJourneyDialog = () => {
		setDeleteJourneyOpen(false);
		setDeleteJourneyUserId("");
		setDeleteJourneyPhone("");
	};

	// ── Archive User handlers ─────────────────────────────────────────────────
	const resetArchiveUserDialog = () => {
		setArchiveUserOpen(false);
		setArchiveUserId("");
		setArchiveUsername("");
		setArchivePreview(null);
		setArchiveError(null);
	};

	const archiveInputsValid = () =>
		!!parseInt(archiveUserId.trim(), 10) && !!archiveUsername.trim();

	/**
	 * Optional dry run — writes nothing; returns the exact rename, the session
	 * count, and the phone-keyed rows the next signup would inherit. Editing
	 * either input clears the result (see the onChange handlers) so a stale
	 * preview is never shown next to changed inputs.
	 */
	const handleArchivePreview = async () => {
		const uid = parseInt(archiveUserId.trim(), 10);
		if (!uid || isNaN(uid)) {
			setArchiveError("User ID must be a number.");
			return;
		}
		if (!archiveUsername.trim()) {
			setArchiveError("Mobile number / username is required.");
			return;
		}
		setArchiveBusy("preview");
		setArchiveError(null);
		setArchivePreview(null);
		try {
			const res = await adminService.archiveUser(uid, archiveUsername.trim(), true);
			setArchivePreview(res.data);
		} catch (error: any) {
			setArchiveError(error.response?.data?.error || "Preview failed.");
		} finally {
			setArchiveBusy(null);
		}
	};

	/**
	 * The real write. Reachable as soon as both inputs are filled — previewing
	 * first is encouraged but not enforced; the backend re-validates the
	 * (user_id, username) pair and every guard on this call too.
	 */
	const handleArchiveUser = async () => {
		const uid = parseInt(archiveUserId.trim(), 10);
		if (!uid || isNaN(uid)) {
			setArchiveError("User ID must be a number.");
			return;
		}
		if (!archiveUsername.trim()) {
			setArchiveError("Mobile number / username is required.");
			return;
		}
		setArchiveBusy("archive");
		setArchiveError(null);
		try {
			const res = await adminService.archiveUser(uid, archiveUsername.trim(), false);
			const { old_username, new_username, sessions_killed } = res.data;
			toast({
				title: "User archived",
				description: `${old_username} → ${new_username}. ${sessions_killed} session(s) killed. "${old_username}" is free for a fresh signup.`,
			});
			resetArchiveUserDialog();
		} catch (error: any) {
			setArchiveError(error.response?.data?.error || "Archive failed.");
		} finally {
			setArchiveBusy(null);
		}
	};

	// ── Edit User ─────────────────────────────────────────────────────────────
	// Looks a borrower up by MOBILE NUMBER only — the mobile number IS their
	// auth_user.username throughout this codebase, so there is no second
	// identifier to type (unlike Archive User, which demands both because it
	// mutates the username itself and a mistype there is unrecoverable).
	//
	// Editable surface is the five identity fields support actually gets
	// corrections for: first/last name on auth_user, and salutation / DOB /
	// gender on IndividualDetail. Business details are shown read-only purely as
	// "is this the right borrower?" confirmation — retyping a PAN or GST would
	// desync the KYC vendor payloads those were verified against.
	const [editUserOpen, setEditUserOpen] = React.useState(false);
	const [editUserPhone, setEditUserPhone] = React.useState("");
	// The row as last read from (or written to) the server. Kept separate from
	// `editUserForm` so the Save button can diff against it and send ONLY the
	// changed keys — an absent key means "leave alone" on the backend.
	const [editUserLoaded, setEditUserLoaded] = React.useState<AdminUserProfile | null>(null);
	const [editUserForm, setEditUserForm] = React.useState<AdminUserProfileUpdate>({});
	const [editUserBusy, setEditUserBusy] = React.useState<"lookup" | "save" | null>(null);
	const [editUserError, setEditUserError] = React.useState<string | null>(null);

	const resetEditUserDialog = () => {
		setEditUserOpen(false);
		setEditUserPhone("");
		setEditUserLoaded(null);
		setEditUserForm({});
		setEditUserError(null);
	};

	const seedEditUserForm = (row: AdminUserProfile) => {
		setEditUserLoaded(row);
		setEditUserForm({
			first_name: row.first_name,
			last_name: row.last_name,
			salutation: row.salutation,
			date_of_birth: row.date_of_birth,
			gender: row.gender,
		});
	};

	const handleEditUserLookup = async () => {
		const phone = editUserPhone.trim();
		if (!phone) {
			setEditUserError("Mobile number is required.");
			return;
		}
		setEditUserBusy("lookup");
		setEditUserError(null);
		// Drop the previous borrower immediately: a failed lookup must never
		// leave the form showing someone else's editable data.
		setEditUserLoaded(null);
		setEditUserForm({});
		try {
			const res = await adminService.lookupUserProfile(phone);
			seedEditUserForm(res.data);
		} catch (error: any) {
			setEditUserError(error.response?.data?.error || "Lookup failed.");
		} finally {
			setEditUserBusy(null);
		}
	};

	/**
	 * Sends only the fields whose value differs from what the server last gave
	 * us, so an untouched field is omitted entirely rather than re-submitted.
	 */
	const handleEditUserSave = async () => {
		if (!editUserLoaded) return;
		const changes: AdminUserProfileUpdate = {};
		(Object.keys(editUserForm) as (keyof AdminUserProfileUpdate)[]).forEach((key) => {
			const next = editUserForm[key] ?? "";
			if (next !== (editUserLoaded[key] ?? "")) changes[key] = next;
		});

		if (Object.keys(changes).length === 0) {
			setEditUserError("Nothing changed.");
			return;
		}

		setEditUserBusy("save");
		setEditUserError(null);
		try {
			const res = await adminService.updateUserProfile(editUserLoaded.username, changes);
			// Re-seed from the response (same shape as the lookup) so the form now
			// reflects committed state and a second save is correctly a no-op.
			seedEditUserForm(res.data);
			toast({
				title: "User updated",
				description: res.data.detail || `${res.data.updated?.length ?? 0} field(s) updated.`,
			});
		} catch (error: any) {
			setEditUserError(error.response?.data?.error || "Update failed.");
		} finally {
			setEditUserBusy(null);
		}
	};

	// ── Unblock Verification ──────────────────────────────────────────────────
	// The two auto-fire verification pages (page 12 Identity, page 15 Final)
	// block a borrower permanently by writing a flag into LoanJourneyState.meta.
	// NameMatchVerification.tsx reads that flag on mount and returns BEFORE
	// firing the checks, so fixing the borrower's data alone changes nothing —
	// the flag has to go for the checks to run again.
	//
	// This resets meta to {}. It is not an override: the checks re-run on the
	// user's next page load and will write the flag straight back if the data
	// still fails. Verification outcomes live in VerificationTracker, not meta,
	// so no audit history is lost.
	const [unblockOpen, setUnblockOpen] = React.useState(false);
	const [unblockPhone, setUnblockPhone] = React.useState("");
	const [unblockPreview, setUnblockPreview] = React.useState<AdminClearJourneyMetaResponse | null>(null);
	const [unblockBusy, setUnblockBusy] = React.useState<"preview" | "clear" | null>(null);
	const [unblockError, setUnblockError] = React.useState<string | null>(null);

	const resetUnblockDialog = () => {
		setUnblockOpen(false);
		setUnblockPhone("");
		setUnblockPreview(null);
		setUnblockError(null);
	};

	/** Dry run — shows the current meta and which block flags are set. Writes nothing. */
	const handleUnblockPreview = async () => {
		const phone = unblockPhone.trim();
		if (!phone) {
			setUnblockError("Mobile number is required.");
			return;
		}
		setUnblockBusy("preview");
		setUnblockError(null);
		setUnblockPreview(null);
		try {
			const res = await adminService.clearJourneyMeta(phone, true);
			setUnblockPreview(res.data);
		} catch (error: any) {
			setUnblockError(error.response?.data?.error || "Check failed.");
		} finally {
			setUnblockBusy(null);
		}
	};

	const handleUnblockVerification = async () => {
		const phone = unblockPhone.trim();
		if (!phone) {
			setUnblockError("Mobile number is required.");
			return;
		}
		setUnblockBusy("clear");
		setUnblockError(null);
		try {
			const res = await adminService.clearJourneyMeta(phone, false);
			toast({
				title: "Verification unblocked",
				description:
					res.data.was_blocked_by.length > 0
						? `Cleared ${res.data.was_blocked_by.join(", ")}. Checks re-run on the user's next page load.`
						: "Metadata cleared (no block flags were set).",
			});
			resetUnblockDialog();
		} catch (error: any) {
			setUnblockError(error.response?.data?.error || "Unblock failed.");
		} finally {
			setUnblockBusy(null);
		}
	};

	// ── Reset Journey handlers ────────────────────────────────────────────────
	const resetResetJourneyDialog = () => {
		setResetJourneyOpen(false);
		setResetJourneyIdentifier("");
		setResetJourneyPages(null);
		setResetJourneyTarget("");
		setResetJourneyError(null);
	};

	/**
	 * Turn the single free-text field into the identifier the API expects.
	 *
	 * A 10-digit number IS the username (auth_user.username stores the mobile —
	 * see api_views/caprion/__init__.py's get_or_create(username=phone)), so it
	 * must be sent as `username`, not `user_id`. Anything else numeric is short
	 * enough to be a table ID; anything non-numeric is a username as typed.
	 */
	const buildResetIdentifier = (): { username?: string; user_id?: number } | null => {
		const raw = resetJourneyIdentifier.trim();
		if (!raw) return null;
		if (/^\d{10}$/.test(raw)) return { username: raw };
		if (/^\d+$/.test(raw)) return { user_id: parseInt(raw, 10) };
		return { username: raw };
	};

	/**
	 * Turn a failed Reset Journey request into an operator-readable message.
	 *
	 * A 404 from journey_pages/reset_journey_to_page means one of: no such
	 * username, no LoanJourneyState row, or an empty checkpoint_template. All of
	 * those read as "this user has nothing to reset", so lead with the plain
	 * "User not found" and keep the backend's specific reason after it.
	 */
	const describeResetJourneyError = (error: any, fallback: string): string => {
		const status = error?.response?.status;
		const body = error?.response?.data;
		// A non-JSON body (HTML error/login page) has no `.error` — never surface
		// raw markup to the operator.
		const detail = typeof body === "object" && body ? (body.error || body.detail) : null;

		if (status === 404) {
			return detail || "User not found. Check the mobile number and try again.";
		}
		if (!error?.response) {
			return "Could not reach the server. Check your connection and try again.";
		}
		return detail || `${fallback} (HTTP ${status}).`;
	};

	/**
	 * Load the user's numbered page list. Must run before the dropdown can be
	 * populated, since the pages come from that user's own checkpoint_template
	 * (page 1 == checkpoint_index 0) and differ per journey.
	 */
	const handleLoadJourneyPages = async () => {
		const identifier = buildResetIdentifier();
		if (!identifier) {
			setResetJourneyError("Enter a mobile number or user ID.");
			return;
		}
		setResetJourneyBusy("load");
		setResetJourneyError(null);
		setResetJourneyPages(null);
		setResetJourneyTarget("");
		try {
			const res = await adminService.getJourneyPages(identifier);
			// Validate the shape BEFORE it reaches the render. A 2xx body without
			// a `pages` array (e.g. an auth redirect that axios followed and
			// resolved to an HTML login page) would otherwise blow up the whole
			// dashboard on `resetJourneyPages.pages.map`.
			if (!res.data || !Array.isArray(res.data.pages)) {
				setResetJourneyError("Unexpected response from the server. Please sign in again and retry.");
				return;
			}
			if (res.data.pages.length === 0) {
				setResetJourneyError("This user has no journey pages yet — let them open the journey once.");
				return;
			}
			setResetJourneyPages(res.data);
			// Pre-select where the user currently is, so an accidental submit is
			// a no-op rather than a jump to page 1.
			setResetJourneyTarget(String(res.data.current_page_number));
		} catch (error: any) {
			setResetJourneyError(describeResetJourneyError(error, "Failed to load journey pages"));
		} finally {
			setResetJourneyBusy(null);
		}
	};

	const handleResetJourney = async () => {
		const identifier = buildResetIdentifier();
		const pageNumber = parseInt(resetJourneyTarget, 10);
		if (!identifier || !pageNumber || isNaN(pageNumber)) {
			setResetJourneyError("Load the journey and pick a page first.");
			return;
		}
		setResetJourneyBusy("apply");
		setResetJourneyError(null);
		try {
			const res = await adminService.resetJourneyToPage(identifier, pageNumber, false);
			const { to_page_number, target_page, from_page_number, username } = res.data || {};
			toast({
				title: "Journey updated",
				// target_page is optional-chained: the reset itself already
				// succeeded, so a thinner-than-expected body must not throw here.
				description: `${username ?? "User"}: page ${from_page_number} → page ${to_page_number} (${target_page?.name ?? "—"}). Open the journey URL fresh — a plain refresh will not pick this up.`,
			});
			resetResetJourneyDialog();
		} catch (error: any) {
			setResetJourneyError(describeResetJourneyError(error, "Reset failed"));
		} finally {
			setResetJourneyBusy(null);
		}
	};

	const resetPetpoojaDumpDialog = () => {
		setPetpoojaDumpOpen(false);
		setPetpoojaDumpJson("");
	};

	const fetchDumps = React.useCallback(async (page = 1, search = "") => {
		setDumpsLoading(true);
		try {
			const res = await adminService.listPetpoojaDumps({ page, page_size: 20, search: search || undefined });
			setDumpsList(res.data.results);
			setDumpsTotal(res.data.total);
			setDumpsPage(page);
		} catch {
			toast({ title: "Failed to load dumps", variant: "destructive" });
		} finally {
			setDumpsLoading(false);
		}
	}, [toast]);

	const openDumpsViewer = () => {
		setDumpsViewerOpen(true);
		setDumpsSearch("");
		fetchDumps(1, "");
	};

	const handlePetpoojaDump = async () => {
		const trimmed = petpoojaDumpJson.trim();
		if (!trimmed) {
			toast({ title: "Payload is empty", variant: "destructive" });
			return;
		}
		let parsed: Record<string, any>;
		try {
			parsed = JSON.parse(trimmed);
		} catch {
			toast({ title: "Invalid JSON", description: "Please paste valid JSON before submitting.", variant: "destructive" });
			return;
		}

		// Download a local copy before submitting
		const todayDate = new Date().toISOString().slice(0, 10).replace(/-/g, "");
		const restId = parsed?.restaurant_details?.restaurant_id ?? "unknown";
		const fileName = `PPInputFiles_${todayDate}_${restId}.json`;
		const blob = new Blob([JSON.stringify(parsed, null, 2)], { type: "application/json" });
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement("a");
		anchor.href = url;
		anchor.download = fileName;
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		URL.revokeObjectURL(url);

		setPetpoojaDumpLoading(true);
		try {
			const res = await adminService.petpoojaDump(parsed);
			toast({
				title: "Petpooja dump submitted",
				description: res.data?.message || "Data received and saved.",
			});
			resetPetpoojaDumpDialog();
		} catch (error: any) {
			toast({
				title: "Submission failed",
				description: error.response?.data?.error || "Failed to submit petpooja dump",
				variant: "destructive",
			});
		} finally {
			setPetpoojaDumpLoading(false);
		}
	};

	const handleDeleteUserJourney = async () => {
		const uid = parseInt(deleteJourneyUserId.trim(), 10);
		if (!uid || isNaN(uid)) {
			toast({ title: "Invalid user ID", variant: "destructive" });
			return;
		}
		if (!deleteJourneyPhone.trim()) {
			toast({ title: "Phone number is required", variant: "destructive" });
			return;
		}
		setDeleteJourneyLoading(true);
		try {
			const res = await adminService.deleteUserJourney(uid, deleteJourneyPhone.trim());
			const { mappings_deleted, bap_leads_deleted } = res.data;
			toast({
				title: "Journey deleted",
				description: `Removed ${mappings_deleted} mapping(s) and ${bap_leads_deleted} BAP lead(s) for user ID ${uid}.`,
			});
			resetDeleteJourneyDialog();
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.response?.data?.error || "Failed to delete journey",
				variant: "destructive",
			});
		} finally {
			setDeleteJourneyLoading(false);
		}
	};

	const todayStr = new Date().toISOString().split('T')[0];
	const tomorrow = new Date();
	tomorrow.setDate(tomorrow.getDate() + 1);
	const tomorrowStr = tomorrow.toISOString().split('T')[0];

	React.useEffect(() => {
		const fetchCrmSettings = async () => {
			if (!isInternalUser) return;
			try {
				const res = await crmService.getUserSettings();
				const displayModeSetting = res.data.find((s: any) => s.property_name === "lead_form_display_mode");
				if (displayModeSetting) {
					setLeadFormDisplayMode(displayModeSetting.property_value as "single" | "story");
				}
				const tableModeSetting = res.data.find((s: any) => s.property_name === "lead_table_display_mode");
				if (tableModeSetting) {
					setLeadTableDisplayMode(tableModeSetting.property_value as "excel" | "modern");
				}
				const timezoneSetting = res.data.find((s: any) => s.property_name === "timezone");
				if (timezoneSetting) {
					setTimezone(timezoneSetting.property_value);
				}
				const layoutSetting = res.data.find((s: any) => s.property_name === "layout");
				if (layoutSetting) {
					setLayout(layoutSetting.property_value as "compact" | "standard" | "spacious");
				}
			} catch (error) {
				console.error("Failed to fetch CRM settings:", error);
			}
		};
		fetchCrmSettings();
	}, [isInternalUser]);

	const updateLeadFormDisplayMode = async (mode: "single" | "story") => {
		setLeadFormDisplayMode(mode);
		try {
			await crmService.saveUserSetting({
				property_name: "lead_form_display_mode",
				property_value: mode
			});
			toast({ title: "Preference saved" });
		} catch (error) {
			toast({ title: "Failed to save preference", variant: "destructive" });
		}
	};

	const updateLeadTableDisplayMode = async (mode: "excel" | "modern") => {
		setLeadTableDisplayMode(mode);
		try {
			await crmService.saveUserSetting({
				property_name: "lead_table_display_mode",
				property_value: mode
			});
			toast({ title: "Preference saved" });
		} catch (error) {
			toast({ title: "Failed to save preference", variant: "destructive" });
		}
	};

	const updateTimezone = async (val: string) => {
		setTimezone(val);
		try {
			await crmService.saveUserSetting({
				property_name: "timezone",
				property_value: val
			});
			toast({ title: "Timezone preference saved" });
		} catch (error) {
			toast({ title: "Failed to save timezone preference", variant: "destructive" });
		}
	};

	const updateLayout = async (val: "compact" | "standard" | "spacious") => {
		setLayout(val);
		try {
			await crmService.saveUserSetting({
				property_name: "layout",
				property_value: val
			});
			toast({ title: "Layout preference saved" });
		} catch (error) {
			toast({ title: "Failed to save layout preference", variant: "destructive" });
		}
	};

	const handleDeleteLeads = async () => {
		// First confirmation
		const confirm1 = window.prompt("⚠️ WARNING: This will permanently delete ALL CRM data!\n\nDo you want to delete? (yes/no)");
		if (confirm1?.toLowerCase() !== 'yes') {
			toast({ title: "Cancelled", description: "First confirmation failed." });
			return;
		}

		// Second confirmation
		const confirm2 = window.prompt("❓ Will you repent this later? (yes/no)");
		if (confirm2?.toLowerCase() !== 'no') {
			toast({ title: "Cancelled", description: "Second confirmation failed." });
			return;
		}

		// Third confirmation
		const confirm3 = window.prompt("🚨 FINAL WARNING: Type 'delete' to execute permanent deletion:");
		if (confirm3?.toLowerCase() !== 'delete') {
			toast({ title: "Cancelled", description: "Third confirmation failed." });
			return;
		}

		try {
			await crmService.deleteAllLeads();
			toast({
				title: "Success",
				description: "All CRM leads have been deleted.",
			});
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.response?.data?.error || "Failed to delete leads",
				variant: "destructive",
			});
		}
	};

	const navigate = useNavigate();
	const location = useLocation();

	/**
	 * The dedicated, shareable URL for the Disbursed Loans register
	 * (PHASE B5g port of v2 commit 31e683cd, plans/disbursed_loans_dashboard_
	 * plan.md §5.1). The screen has no route of its own -- it is an
	 * `activeView` -- so the route renders this same dashboard and the
	 * pathname forces the view, mirroring the `leadId` effect below.
	 */
	const DISBURSED_LOANS_PATH = "/admin-dashboard/lms/disbursed-loans";
	const isDisbursedLoansPath = location.pathname === DISBURSED_LOANS_PATH;

	React.useEffect(() => {
		if (leadId) {
			setActiveView("crm-leads");
		}
	}, [leadId]);

	// Depends on the pathname rather than running once on mount, so an in-app
	// navigation TO the link (not just a cold load or refresh) also lands on
	// the right view.
	React.useEffect(() => {
		if (isDisbursedLoansPath) {
			setActiveView("lms-disbursed-loans");
		}
	}, [isDisbursedLoansPath]);

	const handleViewChange = (view: AdminView) => {
		// Find item in config to check access
		const allItems = [...SIDEBAR_CONFIG.flatMap(c => c.items), ...FOOTER_CONFIG.flatMap(c => c.items)];
		const item = allItems.find(i => i.id === view);
		
		// If item exists in config, use canAccess, otherwise fall back to legacy or allow (for users/roles)
		if (item) {
			if (!canAccess(item)) return;
		} else {
			// Fallback for views not in sidebar/footer config but handled in dashboard
			const accessMap: Record<string, boolean> = {
				"users": canAccessTools,
				"roles": canAccessTools,
				"general-settings": true
			};
			if (accessMap[view] === false) return;
		}

		if (leadId) {
			navigate("/admin-dashboard", { replace: true });
		}
		// Leaving the Disbursed Loans view must drop its URL, or the address bar
		// would keep claiming "disbursed-loans?search=..." while a completely
		// different screen is on display -- and a refresh would then bounce the
		// user straight back to the register they just left.
		//
		// `location.search` is included because the register mirrors its
		// filters into query params even when it was opened from the sidebar
		// (i.e. on plain /admin-dashboard), and those stale params would
		// otherwise be inherited by whatever view comes next.
		// `replace` keeps the Back button pointing where the user came from.
		if (
			activeView === "lms-disbursed-loans" &&
			view !== "lms-disbursed-loans" &&
			(isDisbursedLoansPath || location.search)
		) {
			navigate("/admin-dashboard", { replace: true });
		}
		setActiveView(view);
		localStorage.setItem('admin_active_view', view);
		if (isMobile) {
			setOpenMobile(false);
		}
	};

	return (
		<div className="flex min-h-screen w-full bg-[#FAF9F6] overflow-hidden">
			<Sidebar className="border-r border-gray-200 shrink-0">
				<SidebarHeader className="p-4 border-b">
					<div className="flex flex-row items-center justify-between">
						<div className="flex items-center gap-2">
							<IntellendLogo size="sm" />
							<span className="font-bold text-lg text-intellend-green">Admin</span>
						</div>
						<Button
							variant="ghost"
							size="icon"
							onClick={toggleSidebar}
							className="h-8 w-8 text-gray-500 hover:text-intellend-green transition-colors"
							title="Hide Sidebar"
						>
							<X className="h-5 w-5" />
						</Button>
					</div>
					{user && (
						<div className="mt-3 flex items-center gap-2 px-1">
							<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-intellend-green/10 text-intellend-green font-bold text-sm select-none">
								{user.username.charAt(0).toUpperCase()}
							</div>
							<div className="min-w-0">
								<p className="text-xs font-semibold text-gray-800 truncate">{user.username}</p>
								<p className="text-[10px] text-gray-400 leading-tight">ID: {user.id}</p>
							</div>
						</div>
					)}
				</SidebarHeader>
				<SidebarContent className="bg-white">
					{SIDEBAR_CONFIG.map((category) => (
						canAccess(category) && (
							<SidebarGroup key={category.title}>
								<SidebarGroupLabel>{category.title}</SidebarGroupLabel>
								<SidebarGroupContent>
									<SidebarMenu>
										{category.items.map((item) => (
											canAccess(item) && !item.hideFromMenu && (
												<SidebarMenuItem key={item.id}>
													<SidebarMenuButton
														onClick={() => handleViewChange(item.id as any)}
														isActive={activeView === item.id}
														className="flex items-center gap-3 px-3 py-2"
													>
														<item.icon className="h-4 w-4 text-intellend-green" />
														<span>{item.label}</span>
													</SidebarMenuButton>
												</SidebarMenuItem>
											)
										))}
									</SidebarMenu>
								</SidebarGroupContent>
							</SidebarGroup>
						)
					))}
				</SidebarContent>
				<SidebarFooter className="p-4 mt-auto border-t bg-white text-clip">
					<SidebarMenu>
						{FOOTER_CONFIG.map((category) => (
							canAccess(category) && (
								<SidebarGroup key={category.title}>
									<SidebarGroupLabel>{category.title}</SidebarGroupLabel>
									<SidebarGroupContent>
										<SidebarMenu>
											{category.items.map((item) => (
												canAccess(item) && !item.hideFromMenu && (
													<SidebarMenuItem key={item.id}>
														<SidebarMenuButton
															onClick={() => handleViewChange(item.id as any)}
															isActive={activeView === item.id}
															className="flex items-center gap-3 px-3 py-2"
														>
															<item.icon className="h-4 w-4 text-intellend-green" />
															<span>{item.label}</span>
														</SidebarMenuButton>
													</SidebarMenuItem>
												)
											))}
										</SidebarMenu>
									</SidebarGroupContent>
								</SidebarGroup>
							)
						))}

						<SidebarMenuItem>
							<SidebarMenuButton onClick={logout} className="flex items-center gap-3 px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50">
								<LogOut className="h-4 w-4" />
								<span>Logout</span>
							</SidebarMenuButton>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarFooter>
			</Sidebar>

			<SidebarInset className="flex-1 bg-[#FAF9F6] w-full overflow-hidden">
			<header className="flex h-16 items-center justify-between gap-2 border-b bg-white/80 backdrop-blur-md px-3 sm:px-6 sticky top-0 z-20 w-full">
				<div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
					{((isMobile && !openMobile) || (!isMobile && state === "collapsed")) && (
						<SidebarTrigger className="h-9 w-9 rounded-full bg-intellend-green/10 text-intellend-green hover:bg-intellend-green hover:text-white transition-all" title="Show Sidebar" />
					)}
					<h2 className="text-lg font-semibold text-gray-800 truncate">
							{activeView === "tools" && "IAdmin Tools"}
								{activeView === "iadmin-system" && "System Admin"}
								{activeView === "iadmin-set-journey" && "Set Journey"}
								{activeView === "iadmin-preapproved" && "Manage Pre-Approved"}
								{activeView === "iadmin-whitelist-pincode" && "Add Whitelist Pincodes"}
								{activeView === "iadmin-access" && "Access Control"}
								{activeView === "iadmin-danger" && "Danger Zone"}
								{activeView === "iadmin-fixer" && "Fixer"}
								{activeView === "iadmin-expenses" && "Expenses"}
							{activeView === "analytics" && "C-Suite MIS"}
							{activeView === "crm-leads" && (leadId ? "CRM: Edit Lead" : "CRM: Add Lead")}
							{activeView === "bulk-upload" && "Bulk Upload"}
							{activeView === "leads-table" && "Lead Table"}
							{activeView === "todays-tasks" && "Todays Tasks"}
							{activeView === "tomorrows-tasks" && "Tomorrows Tasks"}
							{activeView === "crm-settings" && "CRM: Settings"}
							{activeView === "general-settings" && "General Settings"}
							{activeView === "crm-config" && canAccessCRMConfig && "CRM: Configuration"}
							{activeView === "journey-analysis" && "User Journeys"}
							{activeView === "caprion-journey-status" && "Caprion: Journey Status"}
							{activeView === "stage-analytics" && "Stage & Page Analytics"}
							{activeView === "journey-url-analysis" && "URL / Time Analysis"}
							{activeView === "link-click" && "Link Click Analytics"}
							{activeView === "users" && "User Management"}
							{activeView === "roles" && "Role Management"}
							{activeView === "lms-staff-ops" && "LMS Staff Operations"}
							{activeView === "lms-staff-permissions" && "LMS Staff Permissions"}
							{activeView === "lms-disbursed-loans" && "Disbursed Loan Details"}
							{activeView === "lms-upload-daily-collection" && "Upload Daily Collection"}
								{activeView === "lms-intraday-bounce" && "Intra-Day Bounce Tracking"}
								{activeView === "thuriyam-dashboard" && "SQL Script Executor"}
						</h2>
				</div>
				<div className="flex items-center gap-4 shrink-0">
					<span className="text-xs sm:text-sm text-gray-500 hidden xs:inline">
						Logged in <span className="hidden sm:inline">as</span> <span className="font-medium text-gray-900">{user?.username}</span>
					</span>
				</div>
			</header>


				<main className="flex flex-1 flex-col p-4 sm:p-8 min-w-0 max-w-full overflow-x-hidden transition-all duration-300">
					{activeView === "crm-leads" && <CRMLeadForm displayMode={leadFormDisplayMode === "story" ? "diary-mode" : "single-page"} leadId={leadId} />}
					{activeView === "leads-table" && <CRMLeadTable displayMode={leadTableDisplayMode} />}
					{activeView === "todays-tasks" && (
						<CRMLeadTable 
							key="todays-tasks"
							displayMode={leadTableDisplayMode} 
							initialDateFilterType="on_or_before"
							initialDateValue={todayStr}
							title="Todays Tasks"
							hideDateFilters={true}
						/>
					)}
					{activeView === "tomorrows-tasks" && (
						<CRMLeadTable 
							key="tomorrows-tasks"
							displayMode={leadTableDisplayMode} 
							initialDateFilterType="on"
							initialDateValue={tomorrowStr}
							title="Tomorrows Tasks"
							hideDateFilters={true}
						/>
					)}
					{activeView === "bulk-upload" && <CRMBulkUpload />}
					{activeView === "crm-config" && canAccessCRMConfig && <CRMConfig />}
					{activeView === "crm-settings" && (
						<div className="max-w-2xl mx-auto w-full space-y-6">
							<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
								<h3 className="text-xl font-bold text-gray-900 mb-4">CRM Interface Settings</h3>
								<div className="space-y-6">
									<div className="flex items-center justify-between p-4 rounded-lg border border-gray-100 bg-slate-50">
										<div>
											<p className="font-semibold text-gray-800">Add Lead - Display Mode</p>
											<p className="text-sm text-gray-500">Choose how the lead form is presented</p>
										</div>
										<div className="p-4 bg-blue-50 text-blue-700 rounded-lg text-sm border border-blue-100">
											<button 
												onClick={() => updateLeadFormDisplayMode("single")}
												className={`px-4 py-2 text-sm font-medium rounded-md text-center min-w-[130px] transition-all ${leadFormDisplayMode === "single" ? "bg-white text-intellend-green shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
											>
												Single Page
											</button>
											<button 
												onClick={() => updateLeadFormDisplayMode("story")}
												className={`px-4 py-2 text-sm font-medium rounded-md text-center min-w-[130px] transition-all ${leadFormDisplayMode === "story" ? "bg-white text-intellend-green shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
											>
												Diary Mode
											</button>
										</div>
									</div>

									<div className="flex items-center justify-between p-4 rounded-lg border border-gray-100 bg-slate-50">
										<div>
											<p className="font-semibold text-gray-800">Lead Table - Display Mode</p>
											<p className="text-sm text-gray-500">Choose how the CRM lead table is presented.</p>
										</div>
										<div className="p-4 bg-blue-50 text-blue-700 rounded-lg text-sm border border-blue-100">
											<button
												onClick={() => updateLeadTableDisplayMode("excel")}
												className={`px-4 py-2 text-sm font-medium rounded-md text-center min-w-[130px] transition-all ${leadTableDisplayMode === "excel" ? "bg-white text-intellend-green shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
											>
												Excel
											</button>
											<button
												onClick={() => updateLeadTableDisplayMode("modern")}
												className={`px-4 py-2 text-sm font-medium rounded-md text-center min-w-[130px] transition-all ${leadTableDisplayMode === "modern" ? "bg-white text-intellend-green shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
											>
												Modern
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
					{activeView === "general-settings" && (
						<div className="max-w-2xl mx-auto w-full space-y-6">
							<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
								<h3 className="text-xl font-bold text-gray-900 mb-4">General Settings</h3>
								<div className="space-y-6">
									<div className="flex items-center justify-between p-4 rounded-lg border border-gray-100 bg-slate-50">
										<div className="flex items-center gap-3">
											<div className="p-2 bg-intellend-green/10 rounded-lg">
												<Zap className="h-4 w-4 text-intellend-green" />
											</div>
											<div>
												<p className="font-semibold text-gray-800">Time Zone</p>
												<p className="text-sm text-gray-500">Select your preferred time zone for dates</p>
											</div>
										</div>
										<div className="w-[220px]">
											<Select value={timezone} onValueChange={updateTimezone}>
												<SelectTrigger className="w-full bg-white border-gray-200">
													<SelectValue placeholder="Select Time Zone" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="+05:30">India (GMT+05:30)</SelectItem>
													<SelectItem value="+00:00">UTC (GMT+00:00)</SelectItem>
													<SelectItem value="+01:00">Europe/London (GMT+01:00)</SelectItem>
													<SelectItem value="-04:00">US Eastern (GMT-04:00)</SelectItem>
													<SelectItem value="-07:00">US Pacific (GMT-07:00)</SelectItem>
													<SelectItem value="+08:00">Singapore/HK (GMT+08:00)</SelectItem>
													<SelectItem value="+09:00">Japan (GMT+09:00)</SelectItem>
													<SelectItem value="+10:00">Australia (GMT+10:00)</SelectItem>
												</SelectContent>
											</Select>
										</div>
									</div>

									<div className="flex items-center justify-between p-4 rounded-lg border border-gray-100 bg-slate-50">
										<div className="flex items-center gap-3">
											<div className="p-2 bg-intellend-green/10 rounded-lg">
												<Square className="h-4 w-4 text-intellend-green" />
											</div>
											<div>
												<p className="font-semibold text-gray-800">Layout Density</p>
												<p className="text-sm text-gray-500">Choose how the data tables are presented</p>
											</div>
										</div>
										<div className="w-[220px]">
											<Select value={layout} onValueChange={(val: any) => updateLayout(val)}>
												<SelectTrigger className="w-full bg-white border-gray-200">
													<SelectValue placeholder="Select Layout" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="compact">Compact</SelectItem>
													<SelectItem value="standard">Standard</SelectItem>
													<SelectItem value="spacious">Spacious</SelectItem>
												</SelectContent>
											</Select>
										</div>
									</div>
								</div>
							</div>

							<div className="flex justify-end pr-2">
								<Button 
									variant="outline" 
									className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300 font-bold shadow-sm"
									onClick={() => {
										updateTimezone("+05:30");
										updateLayout("spacious");
									}}
								>
									Reset to Defaults
								</Button>
							</div>
						</div>
					)}
					{activeView === "users" && <UserManagement />}
					{activeView === "roles" && <RoleManagement />}
					{/*
					  Parallel LMS Staff Management System. The hub self-gates on
					  /api/lms/admin/my_permissions/, and the permissions screen
					  additionally refuses to render for non-superusers (the backend
					  enforces IsSuperUser on every endpoint behind it either way).
					*/}
					{activeView === "lms-staff-ops" && <LmsStaffOpsHub />}
					{activeView === "lms-staff-permissions" && <LmsPermissionsAdmin />}
					{/*
					  Disbursed Loan Details. Sidebar visibility comes from the
					  "lms-disbursed-loans" menu permission; the API behind this
					  screen is separately gated by the "disbursed_loans"
					  LmsStaffPermission tab, and the component renders an
					  explanatory panel on 403 rather than an empty table.
					*/}
					{activeView === "lms-disbursed-loans" && <DisbursedLoansTable />}
					{activeView === "lms-upload-daily-collection" && <UploadDailyCollection />}
						{activeView === "lms-intraday-bounce" && <IntraDayBounceView />}
						{activeView === "thuriyam-dashboard" && <ThuriyamDashboard />}
					{activeView === "tools" && (
							<div className="max-w-3xl w-full space-y-8">
								{/* System Admin */}
								<div>
									<h3 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 px-1">System Admin</h3>
									<div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											asChild
										>
											<a href="/api/intellend/master/" className="overflow-hidden flex items-center">
												<Settings className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Django Admin</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Manage database records and users</div>
												</div>
												<ExternalLink className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</a>
										</Button>
	
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											asChild
										>
											<Link to="/setup/upload/leads" className="overflow-hidden flex items-center">
												<Database className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Upload Leads</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Import bulk BAP lead information</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</Link>
										</Button>
		
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											asChild
										>
											<Link to="/setup/upload/preprocessed" className="overflow-hidden flex items-center">
												<Database className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Upload Preprocessed</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Import petpooja processed data &amp; pre-approved leads</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</Link>
										</Button>
		
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => handleViewChange("iadmin-preapproved")}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Star className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Manage Pre-Approved</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Add or remove pre-approved phone numbers</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => handleViewChange("iadmin-set-journey")}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Route className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Set Journey</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">View and edit a user's loan journey state</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>
	
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => setPetpoojaDumpOpen(true)}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Webhook className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Petpooja Dump</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Submit a raw Petpooja webhook payload</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>
									</div>
								</div>
	
								{/* Caprion Tools */}
								<div>
									<h3 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 px-1">Caprion Tools</h3>
									<div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
										{/* Address Match Report — read-only; no lender call, free to re-run */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => setAddrReportOpen(true)}
										>
											<div className="overflow-hidden flex items-center w-full">
												<MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Address Match Report</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Score all addresses for a mobile number</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										<Dialog open={addrReportOpen} onOpenChange={(open) => { if (!open) resetAddrReportDialog(); else setAddrReportOpen(true); }}>
											<DialogContent className="sm:max-w-4xl flex flex-col max-h-[90vh]">
												<DialogHeader>
													<DialogTitle>Address Match Report</DialogTitle>
													<DialogDescription>
														Every address on file for this borrower — personal, business, Digitap,
														Aadhaar and the selfie GPS pin — scored against each other.
														Read-only: no lender API is called, so this is free to re-run.
													</DialogDescription>
												</DialogHeader>

												{/* Phone tab is the default: it is the existing single-borrower
												    tool and the one used most. The other two are bulk Excel
												    exports -- separate tabs rather than one tab with two inputs,
												    so there is never any doubt about which selection will run. */}
												<Tabs
													defaultValue="phone"
													className="flex flex-col min-h-0 flex-1"
													onValueChange={resetAddrRangeResult}
												>
													<TabsList className="w-full grid grid-cols-3">
														<TabsTrigger value="phone">By phone number</TabsTrigger>
														<TabsTrigger value="range">By user range</TabsTrigger>
														<TabsTrigger value="mobiles">By mobile list</TabsTrigger>
													</TabsList>

													<TabsContent value="phone" className="flex flex-col gap-4 min-h-0 flex-1 mt-4">
														<div className="flex gap-2 items-end">
															<div className="flex-1">
																<label htmlFor="addr-report-mobile" className="text-sm font-medium">Mobile number</label>
																<Input
																	id="addr-report-mobile"
																	placeholder="9876543210"
																	value={addrReportMobile}
																	onChange={(e) => setAddrReportMobile(e.target.value)}
																	onKeyDown={(e) => { if (e.key === "Enter") handleAddressMatchReport(); }}
																	className="mt-1"
																/>
															</div>
															<Button
																onClick={handleAddressMatchReport}
																disabled={addrReportLoading || addrReportMobile.replace(/\D/g, "").length < 10}
															>
																{addrReportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}
															</Button>
														</div>

														{addrReportError && (
															<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
																{addrReportError}
															</div>
														)}

														{addrReportText && (
															<div className="flex flex-col gap-2 min-h-0 flex-1">
																<div className="flex justify-end">
																	<Button variant="outline" size="sm" onClick={copyAddressReport}>
																		{addrReportCopied ? "Copied" : "Copy report"}
																	</Button>
																</div>
																{/* Monospace: the column alignment in the report only reads
																    correctly in a fixed-width font. The ref lets the copy
																    fallback select this element directly on plain-HTTP origins
																    where `navigator.clipboard` does not exist. */}
																<Textarea
																	ref={addrReportRef}
																	readOnly
																	value={addrReportText}
																	className="font-mono text-[11px] leading-snug h-[55vh] resize-none"
																	onFocus={(e) => e.currentTarget.select()}
																/>
															</div>
														)}
													</TabsContent>

													<TabsContent value="range" className="flex flex-col gap-4 min-h-0 flex-1 mt-4">
														<div className="flex gap-2 items-end">
															<div className="flex-1">
																<label htmlFor="addr-range-start" className="text-sm font-medium">From (user #)</label>
																<Input
																	id="addr-range-start"
																	type="number"
																	min={1}
																	placeholder="1"
																	value={addrRangeStart}
																	onChange={(e) => setAddrRangeStart(e.target.value)}
																	className="mt-1"
																/>
															</div>
															<div className="flex-1">
																<label htmlFor="addr-range-end" className="text-sm font-medium">To (user #)</label>
																<Input
																	id="addr-range-end"
																	type="number"
																	min={1}
																	placeholder="50"
																	value={addrRangeEnd}
																	onChange={(e) => setAddrRangeEnd(e.target.value)}
																	className="mt-1"
																/>
															</div>
															<Button onClick={() => handleAddressCpvRangeRun("range")} disabled={addrRangeRunning}>
																{addrRangeRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}
															</Button>
														</div>

														<p className="text-xs text-gray-500">
															Positions in the borrower list (sorted), not user IDs — "1 to 50" is
															the first fifty borrowers. A range longer than the list simply
															returns what exists. Large ranges are fine: this runs in the
															background.
														</p>

														{addrRangeStatusPanel}
													</TabsContent>

													<TabsContent value="mobiles" className="flex flex-col gap-4 min-h-0 flex-1 mt-4">
														<div>
															<label htmlFor="addr-range-mobiles" className="text-sm font-medium">
																Mobile numbers
															</label>
															<Textarea
																id="addr-range-mobiles"
																placeholder="9876543210, 9811111111, 9822222222"
																value={addrRangeMobiles}
																onChange={(e) => setAddrRangeMobiles(e.target.value)}
																className="mt-1 font-mono text-xs h-28 resize-none"
															/>
														</div>

														<div className="flex items-center justify-between gap-3">
															<p className="text-xs text-gray-500">
																Comma, space or newline separated; a +91 prefix is fine.
																Duplicates are ignored, and a number we hold no borrower for is
																still listed with the reason.
															</p>
															<Button
																onClick={() => handleAddressCpvRangeRun("mobiles")}
																disabled={addrRangeRunning || !hasAddrRangeMobiles}
															>
																{addrRangeRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}
															</Button>
														</div>

														{addrRangeStatusPanel}
													</TabsContent>
												</Tabs>
											</DialogContent>
										</Dialog>

										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => setCaprionRpsOpen(true)}
										>
											<div className="overflow-hidden flex items-center w-full">
												<ListOrdered className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Fetch RPS</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Fetch Caprion repayment schedule by application ID</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => setCaprionStatusOpen(true)}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Activity className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Check Status</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Call Caprion CheckStatus by application ID</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>
	
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => { setCaprionCreateLeadResult(null); setCaprionCreateLeadJson(""); setCaprionCreateLeadOpen(true); }}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Zap className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Create Non-Captive Lead</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Submit a new Caprion non-captive loan lead</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>
	
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => { setCaprionCancelResult(null); setCaprionCancelAppId(""); setCaprionCancelApplicantId(""); setCaprionCancelRemarks(""); setCaprionCancelLeadOpen(true); }}
										>
											<div className="overflow-hidden flex items-center w-full">
												<X className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Cancel Lead</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Cancel / withdraw a Caprion loan application</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										{/* Upload Document — pushes files to Caprion's UploadDocument API
												  for an existing application. Grouped with the other green
												  Caprion API tools because that is what it is: a direct
												  passthrough call, logged to CaprionRequestResponse. */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => { resetCaprionUploadDocDialog(); setCaprionUploadDocOpen(true); }}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Upload className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Upload Document</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Send documents to Caprion by application ID</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										{/* Generate Initial Offer — fills a MISSING BRELoanOffers row.
												  Green like the other Caprion tools: it reads the already-stored
												  BRE2 approval and never calls the lender. Idempotent, so it is
												  safe to click even when unsure whether an offer exists. */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => { resetCaprionGenOfferDialog(); setCaprionGenOfferOpen(true); }}
										>
											<div className="overflow-hidden flex items-center w-full">
												<ListOrdered className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Generate Initial Offer</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Generate initial offers if it does not exists in BRE loan offers</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										{/* Re-runs the borrower-facing Digio mandate status check for a mandate
											   that got stuck (typically: user closed the tab during the async NPCI
											   confirmation window), and writes status=4 so the eMandate page shows
											   it as verified. Hits Digio's live API — on-demand only. */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => { resetCaprionVerifyMandateDialog(); setCaprionVerifyMandateOpen(true); }}
										>
											<div className="overflow-hidden flex items-center w-full">
												<ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Trigger Verify Mandates</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Re-check a UPI / NACH mandate and mark it verified</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										{/* Bulk companion to the single-mandate tool above: lists every
										    not-yet-verified PhonePe mandate so the admin does not have to know a
										    mandate id up front. Verifying a row removes it from the list. */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => {
												setCaprionRowNotes({});
												setCaprionManageMandatesOpen(true);
												// Fetch on open (not on mount) so the dashboard does not pay for a
												// query nobody asked for.
												fetchCaprionPendingMandates();
											}}
										>
											<div className="overflow-hidden flex items-center w-full">
												<ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Manage Mandates</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">List unverified PhonePe mandates and verify each one</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										{/* Whitelist pincodes feed the WHITELIST_PINCODE_LOCATION check that
										    runs on the Initial Verification page of the Caprion journey. */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => handleViewChange("iadmin-whitelist-pincode")}
										>
											<div className="overflow-hidden flex items-center w-full">
												<MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Add Whitelist Pincodes</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Manage serviceable pincodes for the location check</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										{/* Neutral/black, not red: only the journey POSITION is rewritten —
										    nothing is deleted and no account is disabled, so this is a
										    reversible navigation fix, not a destructive action. */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-gray-300 hover:bg-gray-50 hover:border-gray-900 min-w-0 group"
											onClick={() => { resetResetJourneyDialog(); setResetJourneyOpen(true); }}
										>
											<div className="overflow-hidden flex items-center w-full">
												<RotateCcw className="h-5 w-5 sm:h-6 sm:w-6 text-gray-900 shrink-0 group-hover:scale-110 transition-transform" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate text-gray-900">Reset Journey</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Move a user to any journey page</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										{/* Pending Dealer Approval — the late-night deferral queue.
										    Neutral styling: it does not delete anything, it releases an
										    approval the system was already going to send at 00:05 IST.
										    The dialog lists only rows that have NOT completed, so an
										    empty list is the normal, healthy state. */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-gray-300 hover:bg-gray-50 hover:border-gray-900 min-w-0 group"
											onClick={() => setPendingDealerApprovalOpen(true)}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Clock className="h-5 w-5 sm:h-6 sm:w-6 text-gray-900 shrink-0 group-hover:scale-110 transition-transform" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate text-gray-900">Pending Dealer Approval</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Approvals held for 12:05 AM — send one now</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										{/* Edit User — corrects the borrower's identity fields in place.
										    Neutral/black like Reset Journey, not red: it overwrites five
										    named columns and deletes nothing. */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-gray-300 hover:bg-gray-50 hover:border-gray-900 min-w-0 group"
											onClick={() => { resetEditUserDialog(); setEditUserOpen(true); }}
										>
											<div className="overflow-hidden flex items-center w-full">
												<UserCog className="h-5 w-5 sm:h-6 sm:w-6 text-gray-900 shrink-0 group-hover:scale-110 transition-transform" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate text-gray-900">Edit User</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Fix name, salutation, DOB or gender by mobile number</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										{/* Unblock Verification — clears LoanJourneyState.meta so the
											   auto-fire verification pages re-run instead of showing the
											   persisted "… Verification Failed" card. Neutral styling: it
											   deletes no data and is not an override — the checks re-run and
											   re-block if the data is still wrong. Pairs with Edit User:
											   fix the data there, then unblock here. */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-gray-300 hover:bg-gray-50 hover:border-gray-900 min-w-0 group"
											onClick={() => { resetUnblockDialog(); setUnblockOpen(true); }}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Unlock className="h-5 w-5 sm:h-6 sm:w-6 text-gray-900 shrink-0 group-hover:scale-110 transition-transform" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate text-gray-900">Unblock Verification</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Clear the block flag so the checks re-run</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>

										{/* View Failures — read-only, per-borrower triage feed over the two
										    tables where a journey failure lands (CaprionRequestResponse +
										    VerificationTracker). Yellow, not green and not red: it reports
										    problems (so it should catch the eye) but mutates nothing and never
										    calls the lender, so it must not look destructive either.
										    Opens empty — nothing is fetched until a mobile number is entered. */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-yellow-300 hover:bg-yellow-50 hover:border-yellow-500 min-w-0 group"
											onClick={() => { resetCaprionFailuresDialog(); setCaprionFailuresOpen(true); }}
										>
											<div className="overflow-hidden flex items-center w-full">
												<AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-500 shrink-0 group-hover:scale-110 transition-transform" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate text-yellow-700">View Failures</div>
													<div className="text-xs sm:text-sm text-yellow-600 font-normal truncate">Failed APIs and verifications by mobile number</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-yellow-400 shrink-0" />
											</div>
										</Button>
	
										{/* Deliberately LAST in this grid: styled red to match the Danger
										    section because, although archiving deletes nothing, it does end
										    the borrower's current journey — so it must neither look like the
										    routine green read-only tools nor sit above them. Subtitle kept
										    short so it does not overflow the fixed-height card. */}
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-red-200 hover:bg-red-50 hover:border-red-500 min-w-0 group"
											onClick={() => { resetArchiveUserDialog(); setArchiveUserOpen(true); }}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Archive className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 shrink-0 group-hover:scale-110 transition-transform" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate text-red-700">Archive User</div>
													<div className="text-xs sm:text-sm text-red-500 font-normal truncate">Free a mobile number — deletes nothing</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-red-300 shrink-0" />
											</div>
										</Button>
									</div>

									{/* ── View Failures dialog ──────────────────────────────────────
									    Two labelled sections. "Page 11" / "Page 15" are the operator's
									    own labels for these feeds — they are NOT a journey-position
									    filter (a page number is a per-user checkpoint_template index),
									    so both sections list ALL matching failures. */}
									<Dialog
										open={caprionFailuresOpen}
										onOpenChange={(open) => {
											if (!open) {
												setCaprionFailuresOpen(false);
												resetCaprionFailuresDialog();
											} else setCaprionFailuresOpen(true);
										}}
									>
										<DialogContent className="sm:max-w-5xl flex flex-col max-h-[90vh]">
											<DialogHeader className="shrink-0">
												<DialogTitle className="flex items-center gap-2">
													<AlertTriangle className="h-5 w-5 text-yellow-500" />
													Caprion — View Failures
												</DialogTitle>
												<DialogDescription>
													Enter a mobile number to see that customer&apos;s failed Caprion
													API calls and failed verifications. Read-only — this reads the
													stored audit trail and never calls Caprion&apos;s live API, so
													looking a number up costs nothing.
												</DialogDescription>
											</DialogHeader>

											{/* Mobile number == auth_user.username. Scopes BOTH sections. */}
											<div className="flex items-center gap-2 shrink-0">
												<Input
													placeholder="Mobile number (e.g. 9876543210)"
													inputMode="numeric"
													value={caprionFailuresMobile}
													onChange={(e) => setCaprionFailuresMobile(e.target.value)}
													onKeyDown={(e) => {
														if (e.key === "Enter") fetchCaprionFailures(caprionFailuresMobile);
													}}
												/>
												<Button
													variant="outline"
													onClick={() => fetchCaprionFailures(caprionFailuresMobile)}
													disabled={caprionFailuresLoading || !caprionFailuresMobile.trim()}
												>
													{caprionFailuresLoading
														? <Loader2 className="h-4 w-4 animate-spin" />
														: <Search className="h-4 w-4" />}
												</Button>
											</div>

											<div className="overflow-y-auto py-2 min-w-0">
												{caprionFailuresLoading ? (
													<div className="flex items-center justify-center py-10 text-gray-500">
														<Loader2 className="h-5 w-5 animate-spin mr-2" />
														Loading failures…
													</div>
												) : caprionFailuresError ? (
													<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
														{caprionFailuresError}
													</div>
												) : !caprionFailures ? (
													/* Nothing searched yet — prompt instead of an empty table, so
													   this never reads as "this customer has no failures". */
													<div className="flex flex-col items-center justify-center py-10 text-center text-gray-500">
														<Search className="h-6 w-6 mb-2 text-gray-400" />
														<p className="text-sm">Enter a mobile number above to view that customer&apos;s failures.</p>
													</div>
												) : caprionFailures.page_11.total === 0 && caprionFailures.page_15.total === 0 ? (
													/* Distinguish "number not found" from "found, but clean" —
													   otherwise a typo looks identical to a healthy customer. */
													<div className="rounded-md bg-green-50 border border-green-200 p-4 text-sm text-green-800">
														{caprionFailures.matched_user_count === 0
															? `No user found for "${caprionFailures.filters.mobile ?? caprionFailuresMobile}" — check the number.`
															: `No failures recorded for ${caprionFailures.filters.mobile ?? caprionFailuresMobile}.`}
													</div>
												) : (
													<div className="space-y-6 min-w-0">
														{/* ── Page 11: failed Caprion API calls ───────────── */}
														<div className="min-w-0">
															<h4 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
																{caprionFailures.page_11.label} · {caprionFailures.page_11.title}
																<Badge variant="secondary" className="ml-2 font-mono">
																	{caprionFailures.page_11.total}
																</Badge>
															</h4>

															{caprionFailures.page_11.results.length === 0 ? (
																<div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
																	No failed Caprion API calls for this customer.
																</div>
															) : (
																<div className="space-y-2 min-w-0">
																	{caprionFailures.page_11.results.map((row: CaprionApiFailureRow) => (
																		<div
																			key={row.id}
																			/* Priority (save_aadhaar_details) is yellow and
																			   bold; every other failed API is muted/black so
																			   it reads as secondary. */
																			className={cn(
																				"rounded-md border p-3 min-w-0",
																				row.is_priority
																					? "border-yellow-300 bg-yellow-50/60"
																					: "border-gray-200 bg-gray-50/60",
																			)}
																		>
																			<div className="flex items-start justify-between gap-2 min-w-0">
																				<div className="min-w-0">
																					<div className="flex items-center gap-2 flex-wrap">
																						<span className={cn(
																							"font-mono text-sm truncate",
																							row.is_priority
																								? "font-semibold text-yellow-800"
																								: "text-gray-700",
																						)}>
																							{row.api_name ?? "—"}
																						</span>
																						{row.is_priority && (
																							<Badge className="bg-yellow-500 text-white text-[10px]">
																								PRIORITY
																							</Badge>
																						)}
																						{row.status_code != null && (
																							<Badge variant="outline" className="font-mono text-[10px]">
																								HTTP {row.status_code}
																							</Badge>
																						)}
																					</div>
																					<div className="text-xs text-gray-500 mt-1 truncate">
																						{row.username ?? "—"}
																						{row.application_id ? ` · app ${row.application_id}` : ""}
																						{row.created_at ? ` · ${new Date(row.created_at).toLocaleString()}` : ""}
																					</div>
																					{row.error_message && (
																						<div className="text-xs text-red-600 mt-1 break-words">
																							{row.error_message}
																						</div>
																					)}
																				</div>
																				{/* Request toggle only — the response is always
																				    on screen below, so there is nothing to
																				    "open" for it. */}
																				<Button
																					variant="ghost"
																					size="sm"
																					className="shrink-0 text-xs"
																					onClick={() => toggleCaprionFailureRequest(row.id)}
																				>
																					{caprionFailuresOpenRequests.has(row.id)
																						? "Hide Request"
																						: "View Request"}
																				</Button>
																			</div>

																			{/* RESPONSE — always visible. This is the lender's
																				   actual reason for the failure (e.g. Caprion code
																				   32), i.e. the thing the operator opened this
																				   screen to read, so it must not be hidden behind
																				   a click. Shown verbatim.
																				   min-w-0 at every level so <pre> wraps instead of
																				   forcing the dialog wider (same containment trick
																				   as CaprionJourneyStatus.tsx). */}
																			<div className="mt-3 min-w-0">
																				<div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
																					Response JSON
																				</div>
																				<pre className="text-[11px] bg-white border rounded p-2 overflow-x-auto whitespace-pre-wrap break-words max-h-64 min-w-0">
																					{row.response_json == null
																						? "— no response recorded —"
																						: JSON.stringify(row.response_json, null, 2)}
																				</pre>
																			</div>

																			{/* REQUEST — behind the toggle, because it is the
																				   bulky half: save_aadhaar_details carries
																				   okyc_xml, which can reach 150k chars and would
																				   bury the response if shown by default. */}
																			{caprionFailuresOpenRequests.has(row.id) && (
																				<div className="mt-3 min-w-0">
																					<div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
																						Request JSON
																					</div>
																					<pre className="text-[11px] bg-white border rounded p-2 overflow-x-auto whitespace-pre-wrap break-words max-h-64 min-w-0">
																						{JSON.stringify(row.request_json, null, 2)}
																					</pre>
																				</div>
																			)}
																		</div>
																	))}
																</div>
															)}
														</div>

														{/* ── Page 15: failed verifications ──────────────── */}
														<div className="min-w-0">
															<h4 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
																{caprionFailures.page_15.label} · {caprionFailures.page_15.title}
																<Badge variant="secondary" className="ml-2 font-mono">
																	{caprionFailures.page_15.total}
																</Badge>
															</h4>

															{caprionFailures.page_15.results.length === 0 ? (
																<div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
																	No failed verifications for this customer.
																</div>
															) : (
																<div className="max-h-[40vh] overflow-y-auto">
																	<Table>
																		<TableHeader>
																			<TableRow>
																				<TableHead>User</TableHead>
																				<TableHead>What failed</TableHead>
																				<TableHead>Message / Remark</TableHead>
																				<TableHead className="w-[150px]">When</TableHead>
																			</TableRow>
																		</TableHeader>
																		<TableBody>
																			{caprionFailures.page_15.results.map((row: VerificationFailureRow) => (
																				<TableRow key={row.id}>
																					<TableCell>
																						<div className="text-xs font-mono truncate">
																							{row.username ?? "—"}
																						</div>
																					</TableCell>
																					<TableCell>
																						<div className="font-medium text-xs">
																							{row.verification_name ?? "—"}
																						</div>
																						<div className="text-[11px] text-gray-500 font-mono break-all">
																							{row.verification_code ?? "—"}
																						</div>
																						<Badge variant="outline" className="mt-1 text-[10px] text-red-600 border-red-200">
																							{row.status ?? "failed"}
																						</Badge>
																					</TableCell>
																					<TableCell>
																						{/* message_to_show is borrower-facing, comment
																						    is the internal remark — show both, since
																						    either one can be the populated one. */}
																						{row.message_to_show && (
																							<div className="text-xs break-words">{row.message_to_show}</div>
																						)}
																						{row.comment && (
																							<div className="text-[11px] text-gray-500 mt-1 break-words">
																								{row.comment}
																							</div>
																						)}
																						{!row.message_to_show && !row.comment && (
																							<span className="text-xs text-gray-400">—</span>
																						)}
																						{row.need_manual_review && !row.manual_review_done && (
																							<Badge className="mt-1 bg-yellow-100 text-yellow-800 text-[10px]">
																								Needs manual review
																							</Badge>
																						)}
																					</TableCell>
																					<TableCell className="text-[11px] text-gray-500">
																						{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
																					</TableCell>
																				</TableRow>
																			))}
																		</TableBody>
																	</Table>
																</div>
															)}
														</div>
													</div>
												)}
											</div>
										</DialogContent>
									</Dialog>

									{/* Pending Dealer Approval dialog */}
									<Dialog open={pendingDealerApprovalOpen} onOpenChange={setPendingDealerApprovalOpen}>
										<DialogContent className="sm:max-w-5xl flex flex-col max-h-[90vh] overflow-y-auto">
											<DialogHeader>
												<DialogTitle>Pending Dealer Approval</DialogTitle>
											</DialogHeader>
											{/* Mounted only while open, so it fetches fresh rows on every
											    open instead of showing a stale list. */}
											{pendingDealerApprovalOpen && <PendingDealerApprovals />}
										</DialogContent>
									</Dialog>

									{/* Caprion Fetch RPS dialog */}
									<Dialog open={caprionRpsOpen} onOpenChange={(open) => { if (!open) { setCaprionRpsOpen(false); setCaprionRpsResult(null); setCaprionRpsAppId(""); setCaprionRpsViewMode("formatted"); } else setCaprionRpsOpen(true); }}>
										<DialogContent className="sm:max-w-2xl flex flex-col max-h-[90vh]">
											<DialogHeader className="shrink-0">
												<DialogTitle className="flex items-center gap-2">
													<ListOrdered className="h-5 w-5 text-intellend-green" />
													Caprion — Fetch Repayment Schedule
												</DialogTitle>
											</DialogHeader>
											<div className="py-2 space-y-3 flex flex-col min-h-0 overflow-y-auto flex-1">
												<div className="flex gap-2">
													<Input
														placeholder="Caprion application_id"
														value={caprionRpsAppId}
														onChange={(e) => setCaprionRpsAppId(e.target.value)}
														onKeyDown={(e) => e.key === "Enter" && handleCaprionFetchRps()}
														className="flex-1"
													/>
													<Button onClick={handleCaprionFetchRps} disabled={caprionRpsLoading || !caprionRpsAppId.trim()}>
														{caprionRpsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Fetch"}
													</Button>
												</div>
												{caprionRpsResult && (
													<div className="mt-2 space-y-2">
														{caprionRpsResult.error ? (
															<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{caprionRpsResult.error}</div>
														) : (
															<>
																{/* View mode toggle — always shown once we have a result */}
																<div className="flex items-center justify-between">
																	<div className="flex rounded-md border border-gray-200 overflow-hidden text-xs w-fit">
																		<button
																			type="button"
																			className={`px-3 py-1.5 font-medium transition-colors ${caprionRpsViewMode === "formatted" ? "bg-intellend-green text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
																			onClick={() => setCaprionRpsViewMode("formatted")}
																		>
																			Formatted
																		</button>
																		<button
																			type="button"
																			className={`px-3 py-1.5 font-medium transition-colors border-l border-gray-200 ${caprionRpsViewMode === "raw" ? "bg-intellend-green text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
																			onClick={() => setCaprionRpsViewMode("raw")}
																		>
																			Raw JSON
																		</button>
																	</div>
																	{caprionRpsResult.success && caprionRpsResult.data?.schedule && (
																		<span className="text-xs text-gray-400">{caprionRpsResult.data.schedule.length} instalment{caprionRpsResult.data.schedule.length !== 1 ? "s" : ""}</span>
																	)}
																</div>
	
																{caprionRpsViewMode === "raw" ? (
																	<pre className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto whitespace-pre-wrap max-h-[50vh]">{JSON.stringify(caprionRpsResult, null, 2)}</pre>
																) : caprionRpsResult.success && caprionRpsResult.data?.schedule ? (
																	<div className="space-y-2">
																		<div className="flex gap-4 text-xs text-gray-500">
																			{caprionRpsResult.data.loan_code && <span>Loan: <strong>{caprionRpsResult.data.loan_code}</strong></span>}
																			{caprionRpsResult.data.total_amount && <span>Total: <strong>₹{caprionRpsResult.data.total_amount}</strong></span>}
																			{caprionRpsResult.data.outstanding_amount && <span>Outstanding: <strong>₹{caprionRpsResult.data.outstanding_amount}</strong></span>}
																		</div>
	
																		<div className="overflow-auto rounded-md border border-gray-200 max-h-[40vh]">
																			<table className="w-full text-xs">
																				<thead className="bg-gray-50 text-left font-semibold uppercase text-gray-500 sticky top-0 z-10">
																					<tr>
																						<th className="px-3 py-2">#</th>
																						<th className="px-3 py-2">Due Date</th>
																						<th className="px-3 py-2">EMI</th>
																						<th className="px-3 py-2">Principal</th>
																						<th className="px-3 py-2">Interest</th>
																						<th className="px-3 py-2">Status</th>
																						<th className="px-3 py-2">Paid Date</th>
																						<th className="px-3 py-2">Paid Amt</th>
																					</tr>
																				</thead>
																				<tbody>
																					{caprionRpsResult.data.schedule.map((row: any, i: number) => (
																						<tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
																							<td className="px-3 py-2">{row.installment_number ?? i + 1}</td>
																							<td className="px-3 py-2 whitespace-nowrap">{row.due_date ?? "—"}</td>
																							<td className="px-3 py-2 whitespace-nowrap">₹{row.due_amount ?? "—"}</td>
																							<td className="px-3 py-2 whitespace-nowrap">₹{row.principal_amount ?? "—"}</td>
																							<td className="px-3 py-2 whitespace-nowrap">₹{row.interest_amount ?? "—"}</td>
																							<td className="px-3 py-2">
																								<span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${row.payment_status === "Paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
																									{row.payment_status ?? "—"}
																								</span>
																							</td>
																							<td className="px-3 py-2 whitespace-nowrap">{row.paid_date ?? "—"}</td>
																							<td className="px-3 py-2 whitespace-nowrap">₹{row.paid_amount ?? "—"}</td>
																						</tr>
																					))}
																				</tbody>
																			</table>
																		</div>
																	</div>
																) : (
																	/* success=false or unexpected shape — show raw regardless of toggle */
																	<pre className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto whitespace-pre-wrap max-h-[50vh]">{JSON.stringify(caprionRpsResult, null, 2)}</pre>
																)}
															</>
														)}
													</div>
												)}
											</div>
										</DialogContent>
									</Dialog>

									{/* Caprion Check Status dialog */}
										<Dialog open={caprionStatusOpen} onOpenChange={(open) => { if (!open) { setCaprionStatusOpen(false); setCaprionStatusResult(null); setCaprionStatusValue(""); } else setCaprionStatusOpen(true); }}>
											<DialogContent className="sm:max-w-2xl">
												<DialogHeader>
													<DialogTitle className="flex items-center gap-2">
														<Activity className="h-5 w-5 text-intellend-green" />
														Caprion — Check Status
													</DialogTitle>
												</DialogHeader>
												<div className="py-2 space-y-3">
													{/* Mode toggle */}
													<div className="flex rounded-md border border-gray-200 overflow-hidden w-fit text-sm">
														<button
															type="button"
															className={`px-3 py-1.5 font-medium transition-colors ${caprionStatusMode === "application_id" ? "bg-intellend-green text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
															onClick={() => { setCaprionStatusMode("application_id"); setCaprionStatusValue(""); setCaprionStatusResult(null); }}
														>
															application_id
														</button>
														<button
															type="button"
															className={`px-3 py-1.5 font-medium transition-colors border-l border-gray-200 ${caprionStatusMode === "urn" ? "bg-intellend-green text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
															onClick={() => { setCaprionStatusMode("urn"); setCaprionStatusValue(""); setCaprionStatusResult(null); }}
														>
															urn
														</button>
													</div>
													<div className="flex gap-2">
														<Input
															placeholder={caprionStatusMode === "application_id" ? "Caprion application_id" : "Caprion URN"}
															value={caprionStatusValue}
															onChange={(e) => setCaprionStatusValue(e.target.value)}
															onKeyDown={(e) => e.key === "Enter" && handleCaprionCheckStatus()}
															className="flex-1"
														/>
														<Button onClick={handleCaprionCheckStatus} disabled={caprionStatusLoading || !caprionStatusValue.trim()}>
															{caprionStatusLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
														</Button>
													</div>
													{caprionStatusResult && (
														<div className="mt-2">
															{caprionStatusResult.error ? (
																<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{caprionStatusResult.error}</div>
															) : (
																<pre className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto whitespace-pre-wrap max-h-96">{JSON.stringify(caprionStatusResult, null, 2)}</pre>
															)}
														</div>
													)}
												</div>
											</DialogContent>
									</Dialog>
	
									{/* Caprion Create Non-Captive Lead dialog */}
									<Dialog open={caprionCreateLeadOpen} onOpenChange={(open) => { if (!open) { setCaprionCreateLeadOpen(false); setCaprionCreateLeadResult(null); setCaprionCreateLeadJson(""); } else setCaprionCreateLeadOpen(true); }}>
										<DialogContent className="sm:max-w-2xl">
											<DialogHeader>
												<DialogTitle className="flex items-center gap-2">
													<Zap className="h-5 w-5 text-intellend-green" />
													Caprion — Create Non-Captive Lead
												</DialogTitle>
												<DialogDescription>
													Paste the full JSON payload for the Caprion <strong>NonCaptiveLead</strong> API. Required fields: <code>urn</code>, <code>loan_type</code>, <code>application_date</code>, <code>amount</code>, <code>scheme_details</code>, <code>personal_details</code>, <code>current_address_details</code>, <code>permanent_address_details</code>, <code>professional_details</code>.
												</DialogDescription>
											</DialogHeader>
											<div className="py-2 space-y-2">
												<Textarea
													placeholder={`{\n  "urn": "INTELLEND-001",\n  "loan_type": "PL",\n  "application_date": "2026-01-01",\n  "amount": 50000,\n  "scheme_details": { ... },\n  "personal_details": { ... },\n  ...\n}`}
													value={caprionCreateLeadJson}
													onChange={(e) => setCaprionCreateLeadJson(e.target.value)}
													className="font-mono text-xs min-h-[260px] resize-y"
													spellCheck={false}
												/>
												{caprionCreateLeadResult && (
													<div className="mt-2">
														{caprionCreateLeadResult.error ? (
															<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{caprionCreateLeadResult.error}</div>
														) : (
															<pre className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto whitespace-pre-wrap max-h-72">{JSON.stringify(caprionCreateLeadResult, null, 2)}</pre>
														)}
													</div>
												)}
											</div>
											<DialogFooter className="gap-2">
												<Button variant="outline" onClick={() => { setCaprionCreateLeadOpen(false); setCaprionCreateLeadResult(null); setCaprionCreateLeadJson(""); }} disabled={caprionCreateLeadLoading}>
													Cancel
												</Button>
												<Button
													onClick={handleCaprionCreateLead}
													disabled={caprionCreateLeadLoading || !caprionCreateLeadJson.trim()}
													className="bg-intellend-green hover:bg-intellend-green/90 text-white"
												>
													{caprionCreateLeadLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating…</> : "Create Lead"}
												</Button>
											</DialogFooter>
										</DialogContent>
									</Dialog>
	
									{/* Caprion Manage Mandates dialog — list + per-row verify */}
									<Dialog open={caprionManageMandatesOpen} onOpenChange={(open) => { if (!open) { setCaprionManageMandatesOpen(false); setCaprionRowNotes({}); } else setCaprionManageMandatesOpen(true); }}>
										<DialogContent className="sm:max-w-4xl">
											<DialogHeader>
												<DialogTitle className="flex items-center gap-2">
													<ShieldCheck className="h-5 w-5 text-intellend-green" />
													Caprion — Manage Mandates
													{!caprionMandateRowsLoading && (
														<Badge variant="secondary" className="ml-1 font-mono">{caprionMandateRows.length}</Badge>
													)}
												</DialogTitle>
												<DialogDescription>
													Unverified mandates for the PhonePe BAP (waiting / processing /
													unknown / not set). Verify calls Digio for that mandate and, if
													Digio confirms it, marks it accepted — the row then drops off this
													list. Rejected and already-accepted mandates are not shown.
												</DialogDescription>
											</DialogHeader>

											<div className="py-2">
												{caprionMandateRowsLoading ? (
													<div className="flex items-center justify-center py-10 text-gray-500">
														<Loader2 className="h-5 w-5 animate-spin mr-2" />
														Loading mandates…
													</div>
												) : caprionMandateRowsError ? (
													<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
														{caprionMandateRowsError}
													</div>
												) : caprionMandateRows.length === 0 ? (
													<div className="rounded-md bg-green-50 border border-green-200 p-4 text-sm text-green-800">
														No unverified mandates — everything is either verified or rejected.
													</div>
												) : (
													<div className="max-h-[55vh] overflow-y-auto">
														<Table>
															<TableHeader>
																<TableRow>
																	<TableHead>User</TableHead>
																	<TableHead>Mandate ID</TableHead>
																	<TableHead className="w-[110px]">Status</TableHead>
																	<TableHead className="w-[90px]">Type</TableHead>
																	<TableHead className="w-[120px] text-right">Action</TableHead>
																</TableRow>
															</TableHeader>
															<TableBody>
																{caprionMandateRows.map((row) => (
																	<TableRow key={row.mandate_id}>
																		<TableCell>
																			<div className="font-medium">{row.user_display ?? "—"}</div>
																			<div className="text-xs text-gray-500 font-mono">
																				{row.username ?? "—"}{row.user_id != null ? ` · #${row.user_id}` : ""}
																			</div>
																		</TableCell>
																		<TableCell>
																			<div className="font-mono text-xs break-all">{row.mandate_id}</div>
																			{/* Shown only when the verify attempt did NOT clear the row,
																			    so a click never looks like it did nothing. */}
																			{caprionRowNotes[row.mandate_id] && (
																				<div className="mt-1 text-xs text-amber-700">
																					{caprionRowNotes[row.mandate_id]}
																				</div>
																			)}
																		</TableCell>
																		<TableCell>
																			<Badge variant="secondary" className="font-mono text-xs">
																				{row.status_label}
																			</Badge>
																		</TableCell>
																		<TableCell className="uppercase text-xs font-mono">
																			{row.mandate_type}
																		</TableCell>
																		<TableCell className="text-right">
																			<Button
																				size="sm"
																				onClick={() => handleCaprionVerifyRow(row)}
																				disabled={caprionVerifyingRowId !== null}
																				className="bg-intellend-green hover:bg-intellend-green/90 text-white"
																			>
																				{caprionVerifyingRowId === row.mandate_id
																					? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Verifying…</>
																					: "Verify"}
																			</Button>
																		</TableCell>
																	</TableRow>
																))}
															</TableBody>
														</Table>
													</div>
												)}
											</div>

											<DialogFooter className="gap-2">
												<Button
													variant="outline"
													onClick={fetchCaprionPendingMandates}
													disabled={caprionMandateRowsLoading || caprionVerifyingRowId !== null}
												>
													{caprionMandateRowsLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Refreshing…</> : "Refresh"}
												</Button>
												<Button
													variant="outline"
													onClick={() => { setCaprionManageMandatesOpen(false); setCaprionRowNotes({}); }}
													disabled={caprionVerifyingRowId !== null}
												>
													Close
												</Button>
											</DialogFooter>
										</DialogContent>
									</Dialog>

									{/* Caprion Trigger Verify Mandates dialog */}
									<Dialog open={caprionVerifyMandateOpen} onOpenChange={(open) => { if (!open) { setCaprionVerifyMandateOpen(false); setCaprionMandateResult(null); } else setCaprionVerifyMandateOpen(true); }}>
										<DialogContent className="sm:max-w-md">
											<DialogHeader>
												<DialogTitle className="flex items-center gap-2">
													<ShieldCheck className="h-5 w-5 text-intellend-green" />
													Caprion — Trigger Verify Mandates
												</DialogTitle>
												<DialogDescription>
													Re-runs the Digio mandate status check and, if Digio confirms the
													mandate, marks it accepted in the DB so the borrower's eMandate
													page shows it as verified. The mandate id is unique, so the user
													and the UPI / NACH type are read from the mandate row itself.
												</DialogDescription>
											</DialogHeader>
											<div className="py-2 space-y-3">
												{/* The Digio mandate id (the same value Caprion calls
												    merchant_request_number). This is the lookup key rather than the
												    UMRN because the UMRN does not exist until NPCI confirms. */}
												<Input
													placeholder="mandate_id (Digio mandate id) *"
													value={caprionMandateId}
													onChange={(e) => setCaprionMandateId(e.target.value)}
													className="font-mono"
												/>
												{/* None = lender resolved from the user's journey (the default, and
												    what the borrower-facing verify endpoints do). Pick a lender only
												    when that resolution has drifted, since the lender decides WHICH
												    Digio credentials are used — wrong creds means Digio answers 403
												    UNAUTHORIZED_ACCESS because the mandate belongs to another account.
												    Values must match Lender.name in the DB. */}
												<select
													value={caprionMandateLender}
													onChange={(e) => setCaprionMandateLender(e.target.value)}
													className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
												>
													<option value="">Lender — None (auto-resolve from user)</option>
													<option value="CAPRION">Caprion</option>
												</select>
												{caprionMandateResult && (
													<div className="mt-2 space-y-2">
														{caprionMandateResult.error ? (
															<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{caprionMandateResult.error}</div>
														) : (
															<>
																{/* Headline outcome first — the raw JSON below is for debugging,
																    but the admin needs to know at a glance whether the DB changed. */}
																<div className={
																	caprionMandateResult.db_updated
																		? "rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800"
																		: caprionMandateResult.is_success
																			? "rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800"
																			: "rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800"
																}>
																	<div className="font-semibold">
																		{caprionMandateResult.is_success ? "Digio confirmed this mandate" : "Not confirmed by Digio yet"}
																	</div>
																	<div className="mt-1">{caprionMandateResult.message}</div>
																	<div className="mt-1 font-mono text-xs">
																		type={String(caprionMandateResult.mandate_type)} · status {String(caprionMandateResult.status_before)} → {String(caprionMandateResult.status_after)}
																		{caprionMandateResult.umrn ? ` · umrn=${caprionMandateResult.umrn}` : ""}
																	</div>
																</div>
																<pre className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto whitespace-pre-wrap max-h-60">{JSON.stringify(caprionMandateResult, null, 2)}</pre>
															</>
														)}
													</div>
												)}
											</div>
											<DialogFooter className="gap-2">
												<Button variant="outline" onClick={() => { setCaprionVerifyMandateOpen(false); setCaprionMandateResult(null); }} disabled={caprionMandateLoading}>
													Close
												</Button>
												<Button
													onClick={handleCaprionVerifyMandate}
													disabled={caprionMandateLoading || !caprionMandateId.trim()}
													className="bg-intellend-green hover:bg-intellend-green/90 text-white"
												>
													{caprionMandateLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Verifying…</> : "Verify Mandate"}
												</Button>
											</DialogFooter>
										</DialogContent>
									</Dialog>

									{/* Caprion Generate Initial Offer dialog */}
									<Dialog open={caprionGenOfferOpen} onOpenChange={(open) => { if (!open) resetCaprionGenOfferDialog(); else setCaprionGenOfferOpen(true); }}>
										<DialogContent className="sm:max-w-lg flex flex-col max-h-[90vh]">
											<DialogHeader className="shrink-0">
												<DialogTitle className="flex items-center gap-2">
													<ListOrdered className="h-5 w-5 text-intellend-green" />
													Caprion — Generate Initial Offer
												</DialogTitle>
												<DialogDescription>
													Generates the initial offer in <strong>BRE Loan Offers</strong> if one does
													not already exist for this application. Built from the application's
													already-stored <strong>BRE2 approval</strong> using the same calculation the
													borrower's journey runs — <strong>Caprion's API is never called</strong>.
													<span className="block mt-1">
														Safe to click if you are unsure: when an offer already exists this
														generates nothing and just tells you so.
													</span>
												</DialogDescription>
											</DialogHeader>

											<div className="py-2 space-y-3 overflow-y-auto flex-1 min-h-0">
												<Input
													placeholder="Caprion application_id (e.g. 1586)"
													value={caprionGenOfferAppId}
													onChange={(e) => { setCaprionGenOfferAppId(e.target.value); setCaprionGenOfferResult(null); }}
													onKeyDown={(e) => { if (e.key === "Enter") handleCaprionGenerateInitialOffer(); }}
													className="font-mono"
													disabled={caprionGenOfferLoading}
												/>

												{caprionGenOfferResult && (
													<div className={`rounded-md border p-3 text-xs ${caprionGenOfferResult.error
														? "border-red-200 bg-red-50 text-red-700"
														: caprionGenOfferResult.created
															? "border-green-200 bg-green-50 text-green-800"
															: "border-amber-200 bg-amber-50 text-amber-800"}`}>
														{caprionGenOfferResult.error ? (
															<div className="font-medium">{caprionGenOfferResult.error}</div>
														) : (
															<>
																<div className="font-medium">
																	{caprionGenOfferResult.created
																		? "Offer generated"
																		: "Offer already existed — nothing generated"}
																</div>
																{caprionGenOfferResult.detail && (
																	<div className="mt-0.5">{caprionGenOfferResult.detail}</div>
																)}
																<div className="mt-1 text-[11px] opacity-80">
																	offer #{caprionGenOfferResult.offer_id}
																	{caprionGenOfferResult.amount && ` · ₹${Number(caprionGenOfferResult.amount).toLocaleString("en-IN")}`}
																	{caprionGenOfferResult.username && ` · ${caprionGenOfferResult.username}`}
																	{caprionGenOfferResult.loan_id && ` · ${caprionGenOfferResult.loan_id}`}
																</div>
															</>
														)}
													</div>
												)}
											</div>

											<DialogFooter className="gap-2 shrink-0">
												<Button variant="outline" onClick={resetCaprionGenOfferDialog} disabled={caprionGenOfferLoading}>
													Close
												</Button>
												<Button
													onClick={handleCaprionGenerateInitialOffer}
													disabled={caprionGenOfferLoading || !caprionGenOfferAppId.trim()}
												>
													{caprionGenOfferLoading
														? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating…</>
														: "Generate"}
												</Button>
											</DialogFooter>
										</DialogContent>
									</Dialog>

									{/* Caprion Upload Document dialog */}
									<Dialog open={caprionUploadDocOpen} onOpenChange={(open) => { if (!open) resetCaprionUploadDocDialog(); else setCaprionUploadDocOpen(true); }}>
										<DialogContent className="sm:max-w-lg flex flex-col max-h-[90vh]">
											<DialogHeader className="shrink-0">
												<DialogTitle className="flex items-center gap-2">
													<Upload className="h-5 w-5 text-intellend-green" />
													Caprion — Upload Document
												</DialogTitle>
												<DialogDescription>
													Sends the selected file(s) to Caprion's <strong>UploadDocument</strong> API as
													<code className="text-xs mx-1">files[]</code>.
													Only the <strong>application ID</strong> is needed — the applicant ID is
													resolved automatically, so a document cannot be filed against the wrong
													borrower. The call is logged against this application ID.
												</DialogDescription>
											</DialogHeader>

											<div className="py-2 space-y-3 overflow-y-auto flex-1 min-h-0">
												<Input
													placeholder="Caprion application_id (e.g. 1651)"
													value={caprionUploadAppId}
													onChange={(e) => { setCaprionUploadAppId(e.target.value); setCaprionUploadResult(null); }}
													className="font-mono"
													disabled={caprionUploadLoading}
												/>
												{/* document_type — searchable dropdown over
												    CAPRION_DOCUMENT_TYPES. `modal` is set on the Popover
												    because this lives INSIDE a Dialog: without it the
												    dialog keeps focus and the combobox's search input
												    cannot be typed into. */}
												<div className="space-y-1">
													<label className="text-xs font-medium text-gray-600">document_type</label>
													<Popover open={caprionDocTypeOpen} onOpenChange={setCaprionDocTypeOpen} modal>
														<PopoverTrigger asChild>
															<Button
																variant="outline"
																role="combobox"
																aria-expanded={caprionDocTypeOpen}
																className="w-full justify-between font-normal"
																disabled={caprionUploadLoading}
															>
																<span className={caprionUploadDocType ? "" : "text-gray-400"}>
																	{caprionUploadDocType || "Select or search a document type…"}
																</span>
																<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
															</Button>
														</PopoverTrigger>
														<PopoverContent
															className="w-[var(--radix-popover-trigger-width)] p-0"
															align="start"
														>
															<Command>
																<CommandInput placeholder="Search document type…" />
																<CommandList>
																	<CommandEmpty>No document type found.</CommandEmpty>
																	<CommandGroup>
																		{CAPRION_DOCUMENT_TYPES.map((type) => (
																			<CommandItem
																				key={type}
																				value={type}
																				onSelect={() => {
																					setCaprionUploadDocType(type);
																					setCaprionUploadResult(null);
																					setCaprionDocTypeOpen(false);
																				}}
																			>
																				<Check
																					className={cn(
																						"mr-2 h-4 w-4",
																						caprionUploadDocType === type ? "opacity-100" : "opacity-0",
																					)}
																				/>
																				{type}
																			</CommandItem>
																		))}
																	</CommandGroup>
																</CommandList>
															</Command>
														</PopoverContent>
													</Popover>
												</div>
												<Input
													placeholder="Remarks (optional)"
													value={caprionUploadRemarks}
													onChange={(e) => setCaprionUploadRemarks(e.target.value)}
													disabled={caprionUploadLoading}
												/>

												{/* `multiple` because Caprion's field is files[] — an operator
												    uploading front+back of an ID should not need two round trips. */}
												<div className="space-y-1">
													<label className="text-xs font-medium text-gray-600">files[]</label>
													<Input
														ref={caprionUploadInputRef}
														type="file"
														multiple
														onChange={(e) => {
															setCaprionUploadFiles(Array.from(e.target.files ?? []));
															setCaprionUploadResult(null);
														}}
														disabled={caprionUploadLoading}
														className="cursor-pointer"
													/>
													{caprionUploadFiles.length > 0 && (
														<ul className="mt-1 space-y-0.5 text-xs text-gray-600">
															{caprionUploadFiles.map((f) => (
																<li key={f.name} className="truncate">
																	• {f.name}{" "}
																	<span className="text-gray-400">({(f.size / 1024).toFixed(0)} KB)</span>
																</li>
															))}
														</ul>
													)}
												</div>

												{caprionUploadResult && (
													<div className={`rounded-md border p-3 text-xs ${caprionUploadResult.error || caprionUploadResult.success === false
														? "border-red-200 bg-red-50 text-red-700"
														: "border-green-200 bg-green-50 text-green-800"}`}>
														{caprionUploadResult.error ? (
															<div className="font-medium">{caprionUploadResult.error}</div>
														) : (
															<>
																<div className="font-medium">
																	{caprionUploadResult.success ? "Upload succeeded" : "Caprion rejected the upload"}
																</div>
																{caprionUploadResult.message && (
																	<div className="mt-0.5">{caprionUploadResult.message}</div>
																)}
																<div className="mt-1 text-[11px] opacity-80">
																	app {caprionUploadResult.application_id} · applicant{" "}
																	{caprionUploadResult.applicant_id}
																	{caprionUploadResult.applicant_id_resolved && " (auto-resolved)"}
																	{caprionUploadResult.code != null && ` · code ${caprionUploadResult.code}`}
																</div>
															</>
														)}
													</div>
												)}
											</div>

											<DialogFooter className="gap-2 shrink-0">
												<Button variant="outline" onClick={resetCaprionUploadDocDialog} disabled={caprionUploadLoading}>
													Close
												</Button>
												<Button
													onClick={handleCaprionUploadDocument}
													disabled={
														caprionUploadLoading
														|| !caprionUploadAppId.trim()
														|| !caprionUploadDocType.trim()
														|| caprionUploadFiles.length === 0
													}
												>
													{caprionUploadLoading
														? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending…</>
														: "Send"}
												</Button>
											</DialogFooter>
										</DialogContent>
									</Dialog>

									{/* Caprion Cancel Lead dialog */}
									<Dialog open={caprionCancelLeadOpen} onOpenChange={(open) => { if (!open) { setCaprionCancelLeadOpen(false); setCaprionCancelResult(null); } else setCaprionCancelLeadOpen(true); }}>
										<DialogContent className="sm:max-w-md">
											<DialogHeader>
												<DialogTitle className="flex items-center gap-2">
													<X className="h-5 w-5 text-intellend-green" />
													Caprion — Cancel Lead
												</DialogTitle>
												<DialogDescription>
													Cancel / withdraw a Caprion loan application. All three fields are required.
												</DialogDescription>
											</DialogHeader>
											<div className="py-2 space-y-3">
												<Input
													placeholder="application_id"
													value={caprionCancelAppId}
													onChange={(e) => setCaprionCancelAppId(e.target.value)}
													className="font-mono"
												/>
												<Input
													placeholder="applicant_id"
													value={caprionCancelApplicantId}
													onChange={(e) => setCaprionCancelApplicantId(e.target.value)}
													className="font-mono"
												/>
												<Input
													placeholder="Remarks (cancellation reason)"
													value={caprionCancelRemarks}
													onChange={(e) => setCaprionCancelRemarks(e.target.value)}
												/>
												{caprionCancelResult && (
													<div className="mt-2">
														{caprionCancelResult.error ? (
															<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{caprionCancelResult.error}</div>
														) : (
															<pre className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto whitespace-pre-wrap max-h-60">{JSON.stringify(caprionCancelResult, null, 2)}</pre>
														)}
													</div>
												)}
											</div>
											<DialogFooter className="gap-2">
												<Button variant="outline" onClick={() => { setCaprionCancelLeadOpen(false); setCaprionCancelResult(null); }} disabled={caprionCancelLoading}>
													Cancel
												</Button>
												<Button
													onClick={handleCaprionCancelLead}
													disabled={caprionCancelLoading || !caprionCancelAppId.trim() || !caprionCancelApplicantId.trim() || !caprionCancelRemarks.trim()}
													className="bg-intellend-green hover:bg-intellend-green/90 text-white"
												>
													{caprionCancelLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Cancelling…</> : "Cancel Lead"}
												</Button>
											</DialogFooter>
										</DialogContent>
									</Dialog>
								</div>

								{/* Access Control */}
								<div>
									<h3 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 px-1">Access Control</h3>
									<div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => handleViewChange("users")}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Users className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">User Management</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">View and filter registered users</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>
	
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => handleViewChange("roles")}
										>
											<div className="overflow-hidden flex items-center w-full">
												<ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Role Management</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Configure system roles and permissions</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>
									</div>
								</div>
	
								{/* Danger */}
								{isAdmin && (
									<div>
										<h3 className="text-xs font-semibold uppercase tracking-widest text-red-400 mb-3 px-1">Danger</h3>
										<div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
											<Button
												variant="outline"
												className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-red-200 hover:bg-red-50 hover:border-red-500 min-w-0 group"
												onClick={handleDeleteLeads}
											>
												<div className="overflow-hidden flex items-center w-full">
													<Trash2 className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 shrink-0 group-hover:scale-110 transition-transform" />
													<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
														<div className="font-semibold truncate text-red-700">Delete All Leads</div>
														<div className="text-xs sm:text-sm text-red-500 font-normal truncate">Permanently remove all CRM records</div>
													</div>
													<ChevronRight className="ml-auto h-4 w-4 text-red-300 shrink-0" />
												</div>
											</Button>
	
											<Button
												variant="outline"
												className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-red-200 hover:bg-red-50 hover:border-red-500 min-w-0 group"
												onClick={() => setDeleteJourneyOpen(true)}
											>
												<div className="overflow-hidden flex items-center w-full">
													<Trash2 className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 shrink-0 group-hover:scale-110 transition-transform" />
													<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
														<div className="font-semibold truncate text-red-700">Delete User Journey</div>
														<div className="text-xs sm:text-sm text-red-500 font-normal truncate">Remove journey data for a specific user</div>
													</div>
													<ChevronRight className="ml-auto h-4 w-4 text-red-300 shrink-0" />
												</div>
											</Button>
										</div>
									</div>
								)}
							</div>
						)}
						{activeView === "iadmin-system" && (
							<div className="max-w-3xl w-full space-y-8">
								<div>
									<h3 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 px-1">System Admin</h3>
									<div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											asChild
										>
											<a href="/api/intellend/master/" className="overflow-hidden flex items-center">
												<Settings className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Django Admin</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Manage database records and users</div>
												</div>
												<ExternalLink className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</a>
										</Button>
		
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											asChild
										>
											<Link to="/setup/upload/leads" className="overflow-hidden flex items-center">
												<Database className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Upload Leads</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Import bulk BAP lead information</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</Link>
										</Button>
		
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => handleViewChange("iadmin-preapproved")}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Star className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Manage Pre-Approved</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Add or remove pre-approved phone numbers</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>
	
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => handleViewChange("iadmin-set-journey")}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Route className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Set Journey</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">View and edit a user's loan journey state</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>
	
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => setPetpoojaDumpOpen(true)}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Webhook className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Petpooja Dump</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Submit a raw Petpooja webhook payload</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>
									</div>
								</div>
							</div>
						)}
						{activeView === "iadmin-set-journey" && <SetJourney />}
						{activeView === "iadmin-preapproved" && <ManagePreApproved />}
						{activeView === "iadmin-whitelist-pincode" && <ManageWhitelistPincode />}
						{activeView === "iadmin-access" && (
							<div className="max-w-3xl w-full space-y-8">
								<div>
									<h3 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 px-1">Access Control</h3>
									<div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => handleViewChange("users")}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Users className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">User Management</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">View and filter registered users</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>
	
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0"
											onClick={() => handleViewChange("roles")}
										>
											<div className="overflow-hidden flex items-center w-full">
												<ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate">Role Management</div>
													<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Configure system roles and permissions</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
											</div>
										</Button>
									</div>
								</div>
							</div>
						)}
						{activeView === "iadmin-danger" && isAdmin && (
							<div className="max-w-3xl w-full space-y-8">
								<div>
									<h3 className="text-xs font-semibold uppercase tracking-widest text-red-400 mb-3 px-1">Danger</h3>
									<div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-red-200 hover:bg-red-50 hover:border-red-500 min-w-0 group"
											onClick={handleDeleteLeads}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Trash2 className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 shrink-0 group-hover:scale-110 transition-transform" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate text-red-700">Delete All Leads</div>
													<div className="text-xs sm:text-sm text-red-500 font-normal truncate">Permanently remove all CRM records</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-red-300 shrink-0" />
											</div>
										</Button>
	
										<Button
											variant="outline"
											className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-red-200 hover:bg-red-50 hover:border-red-500 min-w-0 group"
											onClick={() => setDeleteJourneyOpen(true)}
										>
											<div className="overflow-hidden flex items-center w-full">
												<Trash2 className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 shrink-0 group-hover:scale-110 transition-transform" />
												<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
													<div className="font-semibold truncate text-red-700">Delete User Journey</div>
													<div className="text-xs sm:text-sm text-red-500 font-normal truncate">Remove journey data for a specific user</div>
												</div>
												<ChevronRight className="ml-auto h-4 w-4 text-red-300 shrink-0" />
											</div>
										</Button>
									</div>
								</div>
							</div>
						)}
						{/* Petpooja Dump Dialog */}
						<Dialog open={petpoojaDumpOpen} onOpenChange={(open) => { if (!open) resetPetpoojaDumpDialog(); else setPetpoojaDumpOpen(true); }}>
							<DialogContent className="sm:max-w-2xl">
								<DialogHeader>
									<DialogTitle className="flex items-center gap-2">
										<Webhook className="h-5 w-5 text-intellend-green" />
										Petpooja Dump
									</DialogTitle>
									<DialogDescription>
										Paste a raw Petpooja webhook JSON payload below and click <strong>Submit</strong> to deliver it to the webhook processor. The data will be saved and processed exactly as if it arrived from Petpooja directly.
									</DialogDescription>
								</DialogHeader>
								<div className="py-2 space-y-2">
									<div className="flex justify-end">
										<Button
											variant="outline"
											size="sm"
											className="text-xs gap-1.5 border-intellend-green/40 text-intellend-green hover:bg-intellend-green/5 hover:border-intellend-green"
											onClick={() => setPetpoojaDumpJson(JSON.stringify(shiftToYesterday(PETPOOJA_SAMPLE_DATA), null, 2))}
											disabled={petpoojaDumpLoading}
										>
											Load Sample Data
										</Button>
									</div>
									<Textarea
										placeholder={`{\n  "restaurant_details": {\n    "restaurant_id": "12345",\n    "restaurant_name": "My Restaurant",\n    ...\n  },\n  "form_data": {\n    "firm_type": "Sole Proprietorship",\n    ...\n  }\n}`}
										value={petpoojaDumpJson}
										onChange={(e) => setPetpoojaDumpJson(e.target.value)}
										className="font-mono text-xs min-h-[280px] resize-y"
										spellCheck={false}
									/>
								</div>
								<DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
									<Button
										variant="outline"
										size="sm"
										className="mr-auto text-xs gap-1.5"
										onClick={openDumpsViewer}
										disabled={petpoojaDumpLoading}
									>
										<Database className="h-3.5 w-3.5" />
										View Existing Dumps
									</Button>
									<Button variant="outline" onClick={resetPetpoojaDumpDialog} disabled={petpoojaDumpLoading}>
										Cancel
									</Button>
									<Button
										onClick={handlePetpoojaDump}
										disabled={petpoojaDumpLoading || !petpoojaDumpJson.trim()}
										className="bg-intellend-green hover:bg-intellend-green/90 text-white"
									>
										{petpoojaDumpLoading ? "Submitting…" : "Submit to Webhook"}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
	
						{/* Petpooja Dumps Viewer Sheet */}
							<Sheet open={dumpsViewerOpen} onOpenChange={setDumpsViewerOpen}>
								<SheetContent side="right" className="!w-screen !max-w-none flex flex-col p-0">
									<SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
										<div className="flex items-center justify-between gap-4">
											<SheetTitle className="flex items-center gap-2">
												<Database className="h-5 w-5 text-intellend-green" />
												Petpooja Dump History
												<Badge variant="secondary" className="ml-1 font-mono">{dumpsTotal}</Badge>
											</SheetTitle>
											<Input
												placeholder="Search by restaurant ID, name, or phone…"
												value={dumpsSearch}
												onChange={(e) => {
													setDumpsSearch(e.target.value);
													fetchDumps(1, e.target.value);
												}}
												className="text-sm max-w-sm"
											/>
										</div>
										<SheetDescription>
											Browse previously received Petpooja webhook payloads.
										</SheetDescription>
									</SheetHeader>
		
									<ScrollArea className="flex-1 px-6">
										{dumpsLoading ? (
											<div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
												Loading…
											</div>
										) : dumpsList.length === 0 ? (
											<div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
												No dumps found.
											</div>
										) : (
											<Table className="text-xs mt-4">
												<TableHeader>
													<TableRow>
														<TableHead className="w-[60px]">ID</TableHead>
														<TableHead>Restaurant ID</TableHead>
														<TableHead>Restaurant Name</TableHead>
														<TableHead>Owner Name</TableHead>
														<TableHead>Owner Phone</TableHead>
														<TableHead className="text-right">Received At</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{dumpsList.map((dump) => (
														<TableRow key={dump.id}>
															<TableCell className="font-mono text-muted-foreground">{dump.id}</TableCell>
															<TableCell className="font-mono">{dump.restaurant_id ?? <span className="text-muted-foreground">—</span>}</TableCell>
															<TableCell className="font-medium">{dump.restaurant_name || <span className="text-muted-foreground italic">—</span>}</TableCell>
															<TableCell>{dump.restaurant_owner_name || <span className="text-muted-foreground">—</span>}</TableCell>
															<TableCell className="font-mono">{dump.restaurant_owner_phone || <span className="text-muted-foreground">—</span>}</TableCell>
															<TableCell className="text-right text-muted-foreground whitespace-nowrap">
																{dump.received_at ? new Date(dump.received_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										)}
									</ScrollArea>
		
									{/* Pagination */}
									{dumpsTotal > 20 && (
										<div className="flex items-center justify-between px-6 py-4 border-t shrink-0 text-sm">
											<span className="text-muted-foreground">
												Page {dumpsPage} of {Math.ceil(dumpsTotal / 20)}
											</span>
											<div className="flex gap-2">
												<Button
													variant="outline"
													size="sm"
													disabled={dumpsPage <= 1 || dumpsLoading}
													onClick={() => fetchDumps(dumpsPage - 1, dumpsSearch)}
												>
													Previous
												</Button>
												<Button
													variant="outline"
													size="sm"
													disabled={dumpsPage >= Math.ceil(dumpsTotal / 20) || dumpsLoading}
													onClick={() => fetchDumps(dumpsPage + 1, dumpsSearch)}
												>
													Next
												</Button>
											</div>
										</div>
									)}
								</SheetContent>
							</Sheet>
	
						{/* Delete User Journey Dialog */}
						<Dialog open={deleteJourneyOpen} onOpenChange={(open) => { if (!open) resetDeleteJourneyDialog(); else setDeleteJourneyOpen(true); }}>
							<DialogContent className="sm:max-w-md">
								<DialogHeader>
									<DialogTitle className="text-red-700">Delete User Journey</DialogTitle>
									<DialogDescription>
										Enter the User table ID and the registered phone number. The phone number must match before deletion proceeds.
										This will permanently delete all <strong>BusinessToPersonMapping</strong> and <strong>BAPLeadDetail</strong> records for that user.
									</DialogDescription>
								</DialogHeader>
								<div className="py-2 space-y-3">
									<Input
										type="number"
										placeholder="User ID (e.g. 42)"
										value={deleteJourneyUserId}
										onChange={(e) => setDeleteJourneyUserId(e.target.value)}
										className="font-mono"
										min={1}
									/>
									<Input
										type="text"
										placeholder="Phone number (e.g. 9876543210)"
										value={deleteJourneyPhone}
										onChange={(e) => setDeleteJourneyPhone(e.target.value)}
										className="font-mono"
										maxLength={10}
									/>
								</div>
								<DialogFooter className="gap-2">
									<Button variant="outline" onClick={resetDeleteJourneyDialog}>
										Cancel
									</Button>
									<Button
										variant="destructive"
										onClick={handleDeleteUserJourney}
										disabled={deleteJourneyLoading || !deleteJourneyUserId.trim() || !deleteJourneyPhone.trim()}
									>
										{deleteJourneyLoading ? "Deleting…" : "Delete Journey"}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>

						{/* Edit User Dialog — two phases in one dialog:
						    1. type a mobile number → Search → the form autofills
						    2. edit the fields → Save Changes
						    The form only renders once a borrower has been loaded, so there is
						    never an empty form inviting edits that have nowhere to go. */}
						<Dialog open={editUserOpen} onOpenChange={(open) => { if (!open) resetEditUserDialog(); else setEditUserOpen(true); }}>
							<DialogContent className="sm:max-w-lg flex flex-col max-h-[90vh]">
								<DialogHeader className="shrink-0">
									<DialogTitle className="flex items-center gap-2">
										<UserCog className="h-5 w-5 text-gray-900" />
										Edit User
									</DialogTitle>
									<DialogDescription>
										Enter the borrower's <strong>mobile number</strong> (which is their username) to load
										their details, then correct any field and save. Writes to
										<code className="text-xs mx-1">auth_user</code> and
										<code className="text-xs mx-1">individualdetail</code> only — business details are shown
										for confirmation and are not editable here.
									</DialogDescription>
								</DialogHeader>

								<div className="py-2 space-y-3 overflow-y-auto flex-1 min-h-0">
									{/* Lookup row. Enter submits so the common case is one-handed. */}
									<div className="flex gap-2">
										<Input
											placeholder="Mobile number (e.g. 8817261107)"
											value={editUserPhone}
											onChange={(e) => { setEditUserPhone(e.target.value); setEditUserError(null); }}
											onKeyDown={(e) => { if (e.key === "Enter") handleEditUserLookup(); }}
											className="font-mono flex-1"
											disabled={!!editUserBusy}
										/>
										<Button
											variant="outline"
											onClick={handleEditUserLookup}
											disabled={!!editUserBusy || !editUserPhone.trim()}
										>
											{editUserBusy === "lookup"
												? <Loader2 className="h-4 w-4 animate-spin" />
												: <><Search className="h-4 w-4 mr-1" />Search</>}
										</Button>
									</div>

									{editUserError && (
										<div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
											{editUserError}
										</div>
									)}

									{editUserLoaded && (
										<div className="space-y-3">
											{/* Identity banner — the operator's "right person?" check before
											    they start typing over anything. */}
											<div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 space-y-0.5">
												{/* Username deliberately not shown — it IS the mobile number the
												    operator just typed into the field above, so repeating it is noise. */}
												<div>
													<span className="font-semibold">User ID:</span>{" "}
													<span className="font-mono">{editUserLoaded.user_id}</span>
												</div>
												{editUserLoaded.business ? (
													<div>
														<span className="font-semibold">Business:</span>{" "}
														{editUserLoaded.business.business_name || "—"}
														{editUserLoaded.business.company_pan_number && (
															<> <span className="mx-1 text-gray-300">|</span>
																<span className="font-semibold">PAN:</span>{" "}
																<span className="font-mono">{editUserLoaded.business.company_pan_number}</span>
															</>
														)}
														{editUserLoaded.business.gst_number && (
															<> <span className="mx-1 text-gray-300">|</span>
																<span className="font-semibold">GST:</span>{" "}
																<span className="font-mono">{editUserLoaded.business.gst_number}</span>
															</>
														)}
													</div>
												) : (
													<div className="italic">No business details on record.</div>
												)}
												{editUserLoaded.individual_detail_id === null && (
													<div className="text-amber-700">
														No individual-details row yet — saving will create one.
													</div>
												)}
											</div>

											{/* Salutation + Gender are native <select>s driven by the choices
											    the backend read off the model, matching the existing
											    "Trigger Verify Mandates" lender dropdown in this file. */}
											<div className="grid grid-cols-2 gap-3">
												<div className="space-y-1">
													<label className="text-xs font-medium text-gray-600">Salutation</label>
													<select
														value={editUserForm.salutation ?? ""}
														onChange={(e) => { setEditUserForm(f => ({ ...f, salutation: e.target.value })); setEditUserError(null); }}
														className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
														disabled={!!editUserBusy}
													>
														<option value="">— Not set —</option>
														{editUserLoaded.salutation_choices.map(choice => (
															<option key={choice} value={choice}>{choice}</option>
														))}
													</select>
												</div>
												<div className="space-y-1">
													<label className="text-xs font-medium text-gray-600">Gender</label>
													<select
														value={editUserForm.gender ?? ""}
														onChange={(e) => { setEditUserForm(f => ({ ...f, gender: e.target.value })); setEditUserError(null); }}
														className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
														disabled={!!editUserBusy}
													>
														<option value="">— Not set —</option>
														{/* Stored values are single letters ('m'/'f'/'o'); label them
														    in full so the operator is not guessing. */}
														{editUserLoaded.gender_choices.map(choice => (
															<option key={choice} value={choice}>
																{choice === "m" ? "Male" : choice === "f" ? "Female" : choice === "o" ? "Other" : choice}
															</option>
														))}
													</select>
												</div>
											</div>

											<div className="grid grid-cols-2 gap-3">
												<div className="space-y-1">
													<label className="text-xs font-medium text-gray-600">First Name</label>
													<Input
														value={editUserForm.first_name ?? ""}
														onChange={(e) => { setEditUserForm(f => ({ ...f, first_name: e.target.value })); setEditUserError(null); }}
														disabled={!!editUserBusy}
													/>
												</div>
												<div className="space-y-1">
													<label className="text-xs font-medium text-gray-600">Last Name</label>
													<Input
														value={editUserForm.last_name ?? ""}
														onChange={(e) => { setEditUserForm(f => ({ ...f, last_name: e.target.value })); setEditUserError(null); }}
														disabled={!!editUserBusy}
													/>
												</div>
											</div>

											<div className="space-y-1">
												<label className="text-xs font-medium text-gray-600">Date of Birth</label>
												<Input
													type="date"
													value={editUserForm.date_of_birth ?? ""}
													onChange={(e) => { setEditUserForm(f => ({ ...f, date_of_birth: e.target.value })); setEditUserError(null); }}
													disabled={!!editUserBusy}
												/>
											</div>
										</div>
									)}
								</div>

								<DialogFooter className="gap-2 shrink-0">
									<Button variant="outline" onClick={resetEditUserDialog} disabled={!!editUserBusy}>
										Cancel
									</Button>
									<Button
										onClick={handleEditUserSave}
										disabled={!!editUserBusy || !editUserLoaded}
									>
										{editUserBusy === "save"
											? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving…</>
											: "Save Changes"}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>

						{/* Unblock Verification Dialog — "Check" (dry run) is optional; it
						    shows the stored meta and which block flags are set before the
						    operator commits. */}
						<Dialog open={unblockOpen} onOpenChange={(open) => { if (!open) resetUnblockDialog(); else setUnblockOpen(true); }}>
							<DialogContent className="sm:max-w-lg flex flex-col max-h-[90vh]">
								<DialogHeader className="shrink-0">
									<DialogTitle className="flex items-center gap-2">
										<Unlock className="h-5 w-5 text-gray-900" />
										Unblock Verification
									</DialogTitle>
									<DialogDescription>
										Clears the user's journey <strong>metadata</strong> so the verification pages
										re-run their checks instead of showing the stored
										"<em>Verification Failed</em>" screen. <strong>Nothing is deleted</strong> — the
										verification results themselves are kept.
										<span className="block mt-1">
											This is <strong>not</strong> an override: if the data is still wrong, the
											checks will fail and block the user again. Fix the data first
											(see <strong>Edit User</strong>), then unblock.
										</span>
									</DialogDescription>
								</DialogHeader>

								<div className="py-2 space-y-3 overflow-y-auto flex-1 min-h-0">
									<Input
										placeholder="Mobile number (e.g. 8817261107)"
										value={unblockPhone}
										onChange={(e) => { setUnblockPhone(e.target.value); setUnblockPreview(null); setUnblockError(null); }}
										onKeyDown={(e) => { if (e.key === "Enter") handleUnblockPreview(); }}
										className="font-mono"
										disabled={!!unblockBusy}
									/>

									{unblockError && (
										<div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
											{unblockError}
										</div>
									)}

									{unblockPreview && (
										<div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs space-y-1.5">
											<div className="text-gray-600">
												<span className="font-semibold">User ID:</span>{" "}
												<span className="font-mono">{unblockPreview.user_id}</span>
												<span className="mx-2 text-gray-300">|</span>
												<span className="font-semibold">Stage:</span>{" "}
												<span className="font-mono">{unblockPreview.current_stage}</span>
											</div>
											{/* The whole point of the dry run: tell the operator whether this
											    user is actually blocked, so an unblock that will do nothing is
											    obvious before they click it. */}
											{unblockPreview.was_blocked_by.length > 0 ? (
												<div className="text-red-700">
													<span className="font-semibold">Blocked by:</span>{" "}
													{unblockPreview.was_blocked_by.join(", ")}
												</div>
											) : (
												<div className="text-green-700 font-semibold">
													No block flags set — this user is not blocked.
												</div>
											)}
											<div className="text-gray-600">
												<div className="font-semibold mb-0.5">Current metadata (will become <code>{"{}"}</code>):</div>
												<pre className="bg-white border border-gray-200 rounded p-2 overflow-x-auto text-[11px]">
													{JSON.stringify(unblockPreview.previous_meta, null, 2)}
												</pre>
											</div>
										</div>
									)}
								</div>

								<DialogFooter className="gap-2 shrink-0">
									<Button variant="outline" onClick={resetUnblockDialog} disabled={!!unblockBusy}>
										Cancel
									</Button>
									<Button
										variant="outline"
										onClick={handleUnblockPreview}
										disabled={!!unblockBusy || !unblockPhone.trim()}
									>
										{unblockBusy === "preview"
											? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Checking…</>
											: "Check"}
									</Button>
									<Button
										onClick={handleUnblockVerification}
										disabled={!!unblockBusy || !unblockPhone.trim()}
									>
										{unblockBusy === "clear"
											? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Clearing…</>
											: "Unblock"}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>

						{/* Archive User Dialog — Preview (dry run) is optional; Archive works
						    as soon as both identifiers are filled. */}
						<Dialog open={archiveUserOpen} onOpenChange={(open) => { if (!open) resetArchiveUserDialog(); else setArchiveUserOpen(true); }}>
							<DialogContent className="sm:max-w-lg flex flex-col max-h-[90vh]">
								<DialogHeader className="shrink-0">
									<DialogTitle className="flex items-center gap-2 text-red-700">
										<Archive className="h-5 w-5 text-red-500" />
										Archive User
									</DialogTitle>
									<DialogDescription>
										Frees the mobile number so the same person can sign up again with a fresh journey.
										The username is renamed to <code className="text-xs">&lt;number&gt;_archived_&lt;timestamp&gt;</code> and
										the account is deactivated — <strong>no rows are deleted</strong>.
										Both the User ID and the mobile number are required and must belong to the same account.
										<span className="block mt-1">
											<strong>Preview</strong> is optional — use it to see the exact changes first.
										</span>
									</DialogDescription>
								</DialogHeader>

								<div className="py-2 space-y-3 overflow-y-auto flex-1 min-h-0">
									<Input
										type="number"
										placeholder="User ID (e.g. 148)"
										value={archiveUserId}
										onChange={(e) => { setArchiveUserId(e.target.value); setArchivePreview(null); setArchiveError(null); }}
										className="font-mono"
										min={1}
										disabled={!!archiveBusy}
									/>
									<Input
										type="text"
										placeholder="Mobile number / username (e.g. 8817261107)"
										value={archiveUsername}
										onChange={(e) => { setArchiveUsername(e.target.value); setArchivePreview(null); setArchiveError(null); }}
										className="font-mono"
										disabled={!!archiveBusy}
									/>

									{archiveError && (
										<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
											{archiveError}
										</div>
									)}

									{archivePreview && (
										<div className="rounded-md border border-red-200 bg-red-50/60 p-3 space-y-2 text-sm">
											<div className="font-semibold text-red-800">Preview — nothing has been changed yet</div>
											<dl className="space-y-1 text-xs">
												<div className="flex gap-2">
													<dt className="text-gray-500 w-28 shrink-0">Name</dt>
													<dd className="font-medium">{archivePreview.full_name || "—"}</dd>
												</div>
												<div className="flex gap-2">
													<dt className="text-gray-500 w-28 shrink-0">Username</dt>
													<dd className="font-mono break-all">
														{archivePreview.username} <span className="text-red-700">→</span> {archivePreview.new_username}
													</dd>
												</div>
												<div className="flex gap-2">
													<dt className="text-gray-500 w-28 shrink-0">is_active</dt>
													<dd className="font-mono">{String(archivePreview.is_active)} <span className="text-red-700">→</span> false</dd>
												</div>
												<div className="flex gap-2">
													<dt className="text-gray-500 w-28 shrink-0">Sessions</dt>
													<dd className="font-mono">{archivePreview.active_sessions} will be killed</dd>
												</div>
												{archivePreview.customer_reference_id && (
													<div className="flex gap-2">
														<dt className="text-gray-500 w-28 shrink-0">Ref ID</dt>
														<dd className="font-mono break-all">{archivePreview.customer_reference_id} <span className="text-red-700">→</span> freed</dd>
													</div>
												)}
											</dl>

											{/* These tables are keyed by phone number, not by a user FK, so the
											    next signup on this number reads them. Surfaced so the operator can
											    decide whether stale scores/offers need clearing first. */}
											{archivePreview.inherited_phone_data && Object.keys(archivePreview.inherited_phone_data).length > 0 && (
												<div className="rounded border border-orange-300 bg-orange-50 p-2 text-xs text-orange-800">
													<div className="font-semibold mb-1">⚠️ The next signup on this number will inherit:</div>
													<ul className="list-disc list-inside space-y-0.5 font-mono">
														{Object.entries(archivePreview.inherited_phone_data).map(([label, count]) => (
															<li key={label}>{label}: {String(count)} row(s)</li>
														))}
													</ul>
													<div className="mt-1 font-sans">
														These have no user link — stale BRE scores, offers or pre-approvals can
														carry over into a fresh credit decision. Clear them separately if needed.
													</div>
												</div>
											)}
										</div>
									)}
								</div>

								<DialogFooter className="gap-2 shrink-0">
									<Button variant="outline" onClick={resetArchiveUserDialog} disabled={!!archiveBusy}>
										Cancel
									</Button>
									<Button
										variant="outline"
										onClick={handleArchivePreview}
										disabled={!!archiveBusy || !archiveInputsValid()}
									>
										{archiveBusy === "preview" ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Checking…</> : "Preview"}
									</Button>
									{/* Enabled on valid inputs alone — previewing first is optional. */}
									<Button
										variant="destructive"
										onClick={handleArchiveUser}
										disabled={!!archiveBusy || !archiveInputsValid()}
									>
										{archiveBusy === "archive" ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Archiving…</> : "Archive User"}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>

						{/* Reset Journey Dialog — load the user's pages, then pick one. */}
						<Dialog open={resetJourneyOpen} onOpenChange={(open) => { if (!open) resetResetJourneyDialog(); else setResetJourneyOpen(true); }}>
							<DialogContent className="sm:max-w-lg flex flex-col max-h-[90vh]">
								<DialogHeader className="shrink-0">
									<DialogTitle className="flex items-center gap-2 text-gray-900">
										<RotateCcw className="h-5 w-5 text-gray-900" />
										Reset Journey
									</DialogTitle>
									<DialogDescription>
										Move a user to any page of their journey. Pages are numbered <strong>1&ndash;N</strong> in
										journey order (page&nbsp;1 is the first page, i.e. checkpoint index&nbsp;0).
										Enter the <strong>mobile number</strong> and click <strong>Load Pages</strong> to see
										that user&apos;s actual flow.
									</DialogDescription>
								</DialogHeader>

								<div className="py-2 space-y-3 overflow-y-auto flex-1 min-h-0">
									{/* Free text, not type="number": a 10-digit mobile is the username and
									    must be sent as such, while a shorter number is treated as a user ID.
									    See buildResetIdentifier. */}
									<div className="flex gap-2">
										<Input
											type="text"
											placeholder="Mobile number (e.g. 8817261107) or user ID"
											value={resetJourneyIdentifier}
											onChange={(e) => { setResetJourneyIdentifier(e.target.value); setResetJourneyPages(null); setResetJourneyTarget(""); setResetJourneyError(null); }}
											onKeyDown={(e) => e.key === "Enter" && handleLoadJourneyPages()}
											className="font-mono flex-1"
											disabled={!!resetJourneyBusy}
										/>
										<Button
											variant="outline"
											onClick={handleLoadJourneyPages}
											disabled={!!resetJourneyBusy || !resetJourneyIdentifier.trim()}
										>
											{resetJourneyBusy === "load" ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Loading…</> : "Load Pages"}
										</Button>
									</div>

									{resetJourneyError && (
										<div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
											{resetJourneyError}
										</div>
									)}

									{resetJourneyPages && (
										<div className="space-y-3">
											<div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs space-y-1">
												<div className="flex gap-2">
													<span className="text-gray-500 w-24 shrink-0">User</span>
													<span className="font-mono break-all">{resetJourneyPages.username}</span>
												</div>
												<div className="flex gap-2">
													<span className="text-gray-500 w-24 shrink-0">Currently on</span>
													<span className="font-medium">
														Page {resetJourneyPages.current_page_number} of {resetJourneyPages.total_pages}
														<span className="text-gray-400 font-mono ml-1">(stage {resetJourneyPages.current_stage})</span>
													</span>
												</div>
												<div className="flex gap-2">
													<span className="text-gray-500 w-24 shrink-0">Source</span>
													<span className="font-mono">{resetJourneyPages.source_slug || "—"}</span>
												</div>
											</div>

											<div className="space-y-1.5">
												<label className="text-xs font-medium text-gray-600">Set journey to page</label>
												<Select value={resetJourneyTarget} onValueChange={setResetJourneyTarget} disabled={!!resetJourneyBusy}>
													<SelectTrigger className="w-full bg-white border-gray-200">
														<SelectValue placeholder="Select a page" />
													</SelectTrigger>
													<SelectContent className="max-h-[300px]">
														{/* Guarded: a malformed body must degrade to an empty list,
														    not crash the dashboard. */}
														{(resetJourneyPages.pages ?? []).map((p: any) => (
															<SelectItem key={p.page_number} value={String(p.page_number)}>
																<span className="flex items-center gap-2">
																	<span className="font-mono text-gray-400 w-6 text-right">{p.page_number}.</span>
																	<span>{p.name}</span>
																	{/* Interstitials auto-advance, so landing a user there is
																	    usually not what the operator wants — flag them. */}
																	{p.is_actionable === false && (
																		<span className="text-[10px] uppercase tracking-wide rounded bg-amber-100 text-amber-700 px-1.5 py-0.5">auto</span>
																	)}
																	{p.is_current && (
																		<span className="text-[10px] uppercase tracking-wide rounded bg-blue-100 text-blue-700 px-1.5 py-0.5">current</span>
																	)}
																</span>
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												<p className="text-[11px] text-gray-400">
													<strong className="text-amber-600">auto</strong> = verification / polling page that advances
													on its own (no user action). <strong className="text-blue-600">current</strong> = where the user is now.
												</p>
											</div>

											{(() => {
												const sel = (resetJourneyPages.pages ?? []).find((p: any) => String(p.page_number) === resetJourneyTarget);
												if (!sel) return null;
												return (
													<div className="rounded-md border border-gray-200 p-3 text-xs space-y-1">
														<div className="font-semibold text-gray-700">Page {sel.page_number} — {sel.name}</div>
														<div className="flex gap-2"><span className="text-gray-500 w-28 shrink-0">Route</span><span className="font-mono break-all">{sel.route || "—"}</span></div>
														<div className="flex gap-2"><span className="text-gray-500 w-28 shrink-0">Slug</span><span className="font-mono break-all">{sel.slug || "—"}</span></div>
														<div className="flex gap-2"><span className="text-gray-500 w-28 shrink-0">Journey key</span><span className="font-mono break-all">{sel.journey_key || "—"}</span></div>
														<div className="flex gap-2"><span className="text-gray-500 w-28 shrink-0">Page ID</span><span className="font-mono">{sel.page_id}</span></div>
														<div className="flex gap-2"><span className="text-gray-500 w-28 shrink-0">Checkpoint idx</span><span className="font-mono">{sel.checkpoint_index}</span></div>
														<div className="flex gap-2"><span className="text-gray-500 w-28 shrink-0">Actionable</span><span className="font-mono">{sel.is_actionable ? "yes" : "no (auto-advances)"}</span></div>
														<div className="flex gap-2"><span className="text-gray-500 w-28 shrink-0">Components</span><span className="font-mono">{sel.component_count}</span></div>
													</div>
												);
											})()}

											<div className="rounded-md bg-blue-50 border border-blue-200 p-2.5 text-[11px] text-blue-800">
												After applying, open the journey URL fresh (or in a private window) — a plain
												refresh of the current page will not pick up the new state.
											</div>
										</div>
									)}
								</div>

								<DialogFooter className="gap-2 shrink-0">
									<Button variant="outline" onClick={resetResetJourneyDialog} disabled={!!resetJourneyBusy}>
										Cancel
									</Button>
									<Button
										className="bg-gray-900 hover:bg-gray-800 text-white"
										onClick={handleResetJourney}
										disabled={!!resetJourneyBusy || !resetJourneyPages || !resetJourneyTarget}
									>
										{resetJourneyBusy === "apply" ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Applying…</> : "Set Journey"}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					{activeView === "funnel-dashboard" && <FunnelDashboard />}
					{activeView === "iadmin-fixer" && (
							<FixerPanel />
						)}
					{activeView === "iadmin-expenses" && <ExpensesPanel />}
					{activeView === "analytics" && <AdminAnalytics />}
					{activeView === "link-click" && <LinkClickAnalytics />}
					{activeView === "stage-analytics" && <JourneyStageAnalytics />}
					{activeView === "journey-analysis" && (
						<JourneyAnalysis
							externalSettings={{ timezone, layout }}
							onSettingsChange={(s) => {
								updateTimezone(s.timezone);
								updateLayout(s.layout);
							}}
							onOpenUrlAnalysis={(u) => {
								setUrlAnalysisUserId(u.id);
								handleViewChange("journey-url-analysis");
							}}
						/>
					)}
					{activeView === "journey-url-analysis" && (
						<JourneyUrlTimeAnalysis
							initialUserId={urlAnalysisUserId}
							onBack={() => handleViewChange("journey-analysis")}
						/>
					)}
					{activeView === "caprion-journey-status" && (
						<CaprionJourneyStatus
							onBack={() => handleViewChange("journey-analysis")}
						/>
					)}
				</main>
			</SidebarInset>
		</div>
	);
};

const AdminDashboard = () => {
	return (
		<SidebarProvider>
			<AdminDashboardContent />
		</SidebarProvider>
	);
};

export default AdminDashboard;
